// ------------------------------------------------------------------------------
// <copyright file="scenarioTestingConfig.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * Scenario Testing — Category and scenario definitions.
 *
 * This is a data-only file. No logic lives here.
 *
 * Structure:
 *   SCENARIO_CATEGORIES: Array of category objects, each with:
 *     - id:          Unique string identifier
 *     - name:        Human-readable name
 *     - icon:        lucide-react icon name
 *     - description: Short description shown in sidebar tooltip
 *     - scenarios:   Array of scenario objects
 *
 *   Each scenario:
 *     - id:           Unique string identifier
 *     - name:         Human-readable name
 *     - description:  What this scenario creates and why it's useful
 *     - tags:         Array of string tags for filtering
 *     - variables:    Default variable values (string | () => string for dynamic values)
 *     - steps:        Ordered steps to execute
 *     - expectedState: Object describing the expected billing/plan state for manual verification
 *
 *   Each step:
 *     - id:         Unique step identifier (within the scenario)
 *     - name:       Human-readable step name
 *     - method:     HTTP method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
 *     - path:       API path, e.g. '/auth/register'. May contain {varName} tokens.
 *     - auth:       'none' | 'jwt' | 'function-key'
 *     - body:       Request body object (optional). Values may be {varName} tokens.
 *     - extractors: Map of variableName -> responseFieldPath (e.g. { tenantId: 'tenantId' })
 *     - expect:     { status: number[] } — expected HTTP status codes
 */

// ---------------------------------------------------------------------------
// Category A: User Registration & Auth (5 scenarios)
// ---------------------------------------------------------------------------
const AUTH_CATEGORY = {
  id: 'auth',
  name: 'User Registration & Auth',
  icon: 'Lock',
  description: 'Registration, login, and auth error scenarios.',
  scenarios: [
    {
      id: 'auth-email-fresh',
      name: 'Fresh Email User',
      description: 'Register a new email user. Verifies the full registration flow and that a free-tier plan is assigned by default.',
      tags: ['auth', 'registration', 'fresh'],
      variables: {
        email: () => `e2e-auth-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Auth Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', userId: 'userId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: {
        planTier: 'Free',
        usageCredits: 0,
        canStartSession: true,
      },
    },
    {
      id: 'auth-email-duplicate',
      name: 'Duplicate Email (Error)',
      description: 'Register then re-register the same email. Verifies the 409 Conflict error is handled cleanly.',
      tags: ['auth', 'error', 'duplicate'],
      variables: {
        email: () => `e2e-dup-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Duplicate Test User',
      },
      steps: [
        {
          id: 'register-first',
          name: 'Register First Time',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'register-duplicate',
          name: 'Register Same Email (Expect 409)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          expect: { status: [409] },
        },
      ],
      expectedState: {
        description: 'First registration succeeds. Second returns 409 Conflict.',
      },
    },
    {
      id: 'auth-login-valid',
      name: 'Valid Login',
      description: 'Register then login with correct credentials. Verifies JWT is issued and usable.',
      tags: ['auth', 'login'],
      variables: {
        email: () => `e2e-login-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Login Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'login',
          name: 'Login with Correct Credentials',
          method: 'POST',
          path: '/auth/login/email',
          auth: 'none',
          body: { email: '{email}', password: '{password}' },
          extractors: { jwt: 'token' },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        description: 'JWT token issued. User can authenticate to protected endpoints.',
      },
    },
    {
      id: 'auth-login-wrong-password',
      name: 'Wrong Password (Error)',
      description: 'Register then login with wrong password. Verifies 401 error handling.',
      tags: ['auth', 'login', 'error'],
      variables: {
        email: () => `e2e-badpw-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Bad Password Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'login-wrong',
          name: 'Login with Wrong Password (Expect 401)',
          method: 'POST',
          path: '/auth/login/email',
          auth: 'none',
          body: { email: '{email}', password: 'WrongPassword999!' },
          expect: { status: [401] },
        },
      ],
      expectedState: {
        description: 'Login attempt with wrong password returns 401 Unauthorized.',
      },
    },
    {
      id: 'auth-password-strength',
      name: 'Weak Password (Error)',
      description: 'Register with a weak password ("123"). Verifies password validation.',
      tags: ['auth', 'registration', 'error', 'validation'],
      variables: {
        email: () => `e2e-weakpw-${Date.now()}@test.meetingsync.ai`,
        password: '123',
        name: 'Weak Password Test User',
      },
      steps: [
        {
          id: 'register-weak',
          name: 'Register with Weak Password (Expect 400)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          expect: { status: [400] },
        },
      ],
      expectedState: {
        description: 'Registration rejected with 400 Bad Request. Validation error shown.',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Points-only helpers (Legacy-Cohort-Retirement A2). Every account bills on one rail: monthly
// subscription points, then purchased points. The steps below use only endpoints that are live
// after the retirement; points are read from GET /billing/ios/entitlement (usageCredits).
// ---------------------------------------------------------------------------
const registerStep = () => ({
  id: 'register',
  name: 'Register User',
  method: 'POST',
  path: '/auth/register',
  auth: 'none',
  body: { email: '{email}', password: '{password}', name: '{name}' },
  extractors: { tenantId: 'tenantId', userId: 'userId', jwt: 'token' },
  expect: { status: [200, 201] },
});
const entitlementStep = () => ({
  id: 'entitlement',
  name: 'Get Entitlement (usageCredits)',
  method: 'GET',
  path: '/billing/ios/entitlement',
  auth: 'jwt',
  expect: { status: [200] },
});
const seedSubscriptionStep = (planId, name) => ({
  id: 'seed-subscription',
  name,
  method: 'POST',
  path: '/test/seed/subscription',
  auth: 'function-key',
  body: { tenantId: '{tenantId}', planId, channel: 'zoom', status: 'active' },
  expect: { status: [200] },
});
const userVariables = (prefix, name) => ({
  email: () => `e2e-${prefix}-${Date.now()}@test.meetingsync.ai`,
  password: 'TestPass123!',
  name,
});

// ---------------------------------------------------------------------------
// Category B: Free Tier — Meeting (2 scenarios)
// ---------------------------------------------------------------------------
const FREE_TIER_CATEGORY = {
  id: 'free-tier',
  name: 'Free Tier — Meeting',
  icon: 'Gift',
  description: 'A tenant with no subscription holds no monthly points and spends only purchased points. At zero points every Meeting spend is refused usage_balance_insufficient with the buy_pack remedy — nothing runs free.',
  scenarios: [
    {
      id: 'free-fresh',
      name: 'No Subscription, Zero Points',
      description: 'A newly registered tenant: no subscription, no monthly grant, no purchased points. The entitlement reads zero available points and a Meeting start is refused with the points-pack remedy.',
      tags: ['free', 'fresh', 'points'],
      variables: userVariables('free', 'Free Tier Test User'),
      steps: [registerStep(), entitlementStep()],
      expectedState: {
        planTier: 'Free',
        monthlyPoints: 0,
        purchasedPoints: 0,
        availablePoints: 0,
        canStartSession: false,
        refusal: 'usage_balance_insufficient with options [buy_pack]',
      },
    },
    {
      id: 'free-wire-constants',
      name: 'Entitlement Wire Is Points-Only',
      description: 'The entitlement of a no-subscription tenant carries the constant billingMode "credits" and packStorefront "usage" that older clients still read; the deleted Meeting cap axes report -1.',
      tags: ['free', 'wire', 'points'],
      variables: userVariables('freewire', 'Free Tier Wire User'),
      steps: [registerStep(), entitlementStep()],
      expectedState: {
        billingMode: 'credits',
        packStorefront: 'usage',
        deletedCapAxes: 'live, twoWay, transcription, aiCalls and tts report cap -1',
        canStartSession: false,
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category C: Starter Plan — Meeting (7 scenarios)
// ---------------------------------------------------------------------------
const STARTER_CATEGORY = {
  id: 'starter',
  name: 'Starter Plan — Meeting',
  icon: 'Zap',
  description: 'Starter meeting plan — Zoom: $30/mo, Web: $30/mo, 60 min/mo. 1 language, no TTS.',
  scenarios: [
    {
      id: 'starter-fresh',
      name: 'Fresh Starter ($30/mo)',
      description: 'Starter subscriber with full included hours. Tests the clean Starter dashboard state.',
      tags: ['starter', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-starter-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            productType: 'meeting',
          },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included, 0 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        includedHoursRemaining: '60 min',
        canStartSession: true,
        features: { tts: false, glossary: true, meetingAi: true },
      },
    },
    {
      id: 'starter-half-used',
      name: '50% Included Used',
      description: 'Starter with 30/60 included minutes used. Tests the progress bar at 50%.',
      tags: ['starter', 'subscription', 'partial', 'billing'],
      variables: {
        email: () => `e2e-starterhalf-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter Half User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included, 30 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 30 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        includedHoursRemaining: '30 min',
        canStartSession: true,
      },
    },
    {
      id: 'starter-1min-left',
      name: '1 min Included Left',
      description: 'Starter with 59/60 included minutes used. Tests the near-limit warning banner.',
      tags: ['starter', 'subscription', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-starternear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included, 59 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 59 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        includedHoursRemaining: '1 min',
        canStartSession: true,
        description: 'Near-limit warning banner should be visible.',
      },
    },
    {
      id: 'starter-hours-exhausted',
      name: 'Included Hours Gone',
      description: 'Starter with 60/60 included minutes used. Tests upgrade/purchase CTA display.',
      tags: ['starter', 'subscription', 'exhausted', 'billing'],
      variables: {
        email: () => `e2e-starterout-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter Exhausted User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included, 60 used = exhausted)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 60 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        includedHoursRemaining: '0 min',
        canStartSession: false,
        description: 'Upgrade or Hour Pack purchase CTA should appear.',
      },
    },
    {
      id: 'starter-with-hourpack',
      name: 'Has HourPack (60 min)',
      description: 'Starter with included hours exhausted but 60 min purchased hours available. Tests HourPack as fallback billing source (Priority 1.7).',
      tags: ['starter', 'subscription', 'hourpack', 'billing-priority'],
      variables: {
        email: () => `e2e-starterhp-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter HourPack User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (exhausted included, 60 min purchased)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 60 },
            purchasedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        includedHoursRemaining: '0 min',
        purchasedHoursRemaining: '60 min',
        canStartSession: true,
        description: 'HourPack shown as active billing source after included hours exhausted.',
      },
    },
    {
      id: 'starter-hourpack-1min',
      name: 'HourPack 1 min Left',
      description: 'Starter with HourPack nearly exhausted (59/60 min used). Tests near-limit in purchased hours.',
      tags: ['starter', 'subscription', 'hourpack', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-hpnear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'HourPack Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (exhausted included, HourPack 1 min left)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 60 },
            purchasedHours: { totalMinutes: 60, usedMinutes: 59 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        purchasedHoursRemaining: '1 min',
        canStartSession: true,
        description: 'Near-limit warning on purchased hours.',
      },
    },
    {
      id: 'starter-max-language-blocked',
      name: '2+ Languages (limit=1)',
      description: 'Starter subscriber trying to use 2 languages. Tests the language limit enforcement (Starter allows 1 language).',
      tags: ['starter', 'subscription', 'language-limit', 'billing'],
      variables: {
        email: () => `e2e-starterlang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter Lang Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (Starter)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Starter',
        maxLanguages: 1,
        canStartSession: true,
        description: 'Adding a 2nd language should be blocked by the plan limit (maxTargetLanguages=1).',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category D: Standard Plan — Meeting (7 scenarios)
// ---------------------------------------------------------------------------
const STANDARD_CATEGORY = {
  id: 'standard',
  name: 'Standard Plan — Meeting',
  icon: 'Star',
  description: 'Standard meeting plan — Zoom: $45/mo, Web: $45/mo, 60 min/mo. TTS enabled; Meeting AI funded by points.',
  scenarios: [
    {
      id: 'standard-fresh',
      name: 'Fresh Standard ($45/mo)',
      description: 'Standard subscriber with full 60 min included hours. Tests Standard dashboard: TTS enabled, glossary, Meeting AI funded by points.',
      tags: ['standard', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-std-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included, 0 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Standard',
        includedHoursRemaining: '60 min',
        canStartSession: true,
        features: { tts: true, glossary: true, meetingAi: true },
      },
    },
    {
      id: 'standard-half-used',
      name: '50% Used',
      description: 'Standard with 30/60 included minutes used. Tests progress bar at 50%.',
      tags: ['standard', 'subscription', 'partial', 'billing'],
      variables: {
        email: () => `e2e-stdhalf-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Half User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (30/60 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 30 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', includedHoursRemaining: '30 min', canStartSession: true },
    },
    {
      id: 'standard-1min-left',
      name: '1 min Left',
      description: 'Standard with 59/60 minutes used. Tests near-limit warning state.',
      tags: ['standard', 'subscription', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-stdnear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (59/60 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 59 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', includedHoursRemaining: '1 min', canStartSession: true },
    },
    {
      id: 'standard-exhausted',
      name: 'Hours Exhausted',
      description: 'Standard with 60/60 minutes used. Tests upgrade CTA display.',
      tags: ['standard', 'subscription', 'exhausted', 'billing'],
      variables: {
        email: () => `e2e-stdout-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Exhausted User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60/60 exhausted)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 60 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', includedHoursRemaining: '0 min', canStartSession: false },
    },
    {
      id: 'standard-3-languages',
      name: '3 Languages (at max)',
      description: 'Standard plan at the 3-language limit. Tests at-limit language display.',
      tags: ['standard', 'language-limit', 'billing'],
      variables: {
        email: () => `e2e-stdlang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Lang User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard, 3 langs)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', maxLanguages: 3, description: 'Plan limit is 3 languages. Adding a 4th should be blocked.' },
    },
    {
      id: 'standard-4-languages-blocked',
      name: '4 Languages (over max)',
      description: 'Standard plan attempting 4 languages (over the 3-language limit). Tests over-limit blocked state.',
      tags: ['standard', 'language-limit', 'blocked', 'billing'],
      variables: {
        email: () => `e2e-stdlang4-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard Lang Blocked User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', maxLanguages: 3, description: '4th language blocked by plan limit.' },
    },
    {
      id: 'standard-tts-enabled',
      name: 'TTS Feature Active',
      description: 'Standard subscriber with TTS enabled. Tests that TTS toggle is visible and functional.',
      tags: ['standard', 'tts', 'features', 'billing'],
      variables: {
        email: () => `e2e-stdtts-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard TTS User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard with TTS)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', features: { tts: true }, description: 'TTS toggle should be enabled.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category E: Professional Plan — Meeting (8 scenarios)
// ---------------------------------------------------------------------------
const PROFESSIONAL_CATEGORY = {
  id: 'professional',
  name: 'Professional Plan — Meeting',
  icon: 'Award',
  description: 'Professional meeting plan — Zoom: $80/mo, Web: $80/mo, 300 min/mo. TTS + Meeting AI + glossary.',
  scenarios: [
    {
      id: 'pro-fresh',
      name: 'Fresh Professional ($80/mo)',
      description: 'Professional subscriber with full 300 min included hours. Tests full Pro dashboard: TTS + Meeting AI + glossary.',
      tags: ['professional', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-pro-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (300 min included, 0 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Professional',
        includedHoursRemaining: '300 min',
        canStartSession: true,
        features: { tts: true, glossary: true, meetingAi: true },
      },
    },
    {
      id: 'pro-half-used',
      name: '150 min Used',
      description: 'Professional with 150/300 included minutes used. Tests 50% progress display.',
      tags: ['professional', 'subscription', 'partial', 'billing'],
      variables: {
        email: () => `e2e-prohalf-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Half User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (150/300 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 150 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', includedHoursRemaining: '150 min', canStartSession: true },
    },
    {
      id: 'pro-1min-left',
      name: '1 min Left',
      description: 'Professional with 299/300 minutes used. Tests critical near-limit warning.',
      tags: ['professional', 'subscription', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-pronear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (299/300 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 299 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', includedHoursRemaining: '1 min', canStartSession: true },
    },
    {
      id: 'pro-exhausted',
      name: '300 min Exhausted',
      description: 'Professional with all 300 included minutes used. Tests blocked/upgrade state.',
      tags: ['professional', 'subscription', 'exhausted', 'billing'],
      variables: {
        email: () => `e2e-proout-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Exhausted User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (300/300 exhausted)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 300 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', includedHoursRemaining: '0 min', canStartSession: false },
    },
    {
      id: 'pro-meetingai-active',
      name: 'Meeting AI Available',
      description: 'Professional subscriber verifying Meeting AI tab is enabled. Available on every plan; points fund each call.',
      tags: ['professional', 'meetingai', 'features', 'billing'],
      variables: {
        email: () => `e2e-proai-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro AI User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', features: { meetingAi: true }, description: 'Meeting AI tab should be visible and enabled.' },
    },
    {
      id: 'pro-max-participants-10',
      name: 'At Participant Limit (10)',
      description: 'Professional plan at 10/10 participant limit. Tests limit display.',
      tags: ['professional', 'participant-limit', 'billing'],
      variables: {
        email: () => `e2e-propax-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Max Participants User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', maxParticipants: 10, description: '10/10 participants displayed.' },
    },
    {
      id: 'pro-max-languages-3',
      name: 'At Language Limit (3)',
      description: 'Professional plan at 3/3 language limit. Tests language limit display.',
      tags: ['professional', 'language-limit', 'billing'],
      variables: {
        email: () => `e2e-prolang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro Lang Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', maxLanguages: 3, description: '3/3 languages shown.' },
    },
    {
      id: 'pro-all-features',
      name: 'All Features Enabled',
      description: 'Professional with TTS + glossary + Meeting AI all enabled. Tests full feature set display.',
      tags: ['professional', 'features', 'billing'],
      variables: {
        email: () => `e2e-proall-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro All Features User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (fresh)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Professional',
        features: { tts: true, glossary: true, meetingAi: true },
        canStartSession: true,
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category F: Advanced Plan — Meeting (7 scenarios)
// ---------------------------------------------------------------------------
const ADVANCED_CATEGORY = {
  id: 'advanced',
  name: 'Advanced Plan — Meeting',
  icon: 'Zap',
  description: 'Advanced meeting plan — Zoom: $140/mo, Web: $140/mo, 1,200 min/mo. 10 langs, TTS + Meeting AI.',
  scenarios: [
    {
      id: 'advanced-fresh',
      name: 'Fresh Advanced ($140/mo)',
      description: 'Advanced subscriber with full 1,200 min included hours. Tests full Advanced dashboard.',
      tags: ['advanced', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-adv-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (1200 min included, 0 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Advanced',
        includedHoursRemaining: '1200 min',
        canStartSession: true,
        features: { tts: true, meetingAi: true },
      },
    },
    {
      id: 'advanced-50pct-used',
      name: '50% Used (600 of 1200 min)',
      description: 'Advanced with 600/1,200 included minutes used. Tests 50% progress display.',
      tags: ['advanced', 'subscription', 'partial', 'billing'],
      variables: {
        email: () => `e2e-advhalf-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Half User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (600/1200 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 600 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', includedHoursRemaining: '600 min', canStartSession: true },
    },
    {
      id: 'advanced-near-limit',
      name: '1 min Left (1199 of 1200 used)',
      description: 'Advanced with 1,199/1,200 minutes used. Tests critical near-limit warning.',
      tags: ['advanced', 'subscription', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-advnear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (1199/1200 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 1199 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', includedHoursRemaining: '1 min', canStartSession: true },
    },
    {
      id: 'advanced-exhausted',
      name: '1200 min Exhausted',
      description: 'Advanced with all 1,200 included minutes used. Tests blocked/upgrade state.',
      tags: ['advanced', 'subscription', 'exhausted', 'billing'],
      variables: {
        email: () => `e2e-advout-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Exhausted User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (1200/1200 exhausted)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 1200 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', includedHoursRemaining: '0 min', canStartSession: false },
    },
    {
      id: 'advanced-with-hourpack',
      name: 'With Hour Pack Top-Up',
      description: 'Advanced with all included hours exhausted plus a 60-min hour pack add-on.',
      tags: ['advanced', 'subscription', 'hourpack', 'billing'],
      variables: {
        email: () => `e2e-advpack-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Hour Pack User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (exhausted + 60 min hour pack)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 1200 },
            purchasedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', purchasedHoursRemaining: '60 min', canStartSession: true },
    },
    {
      id: 'advanced-multifeature',
      name: 'Full Feature Check',
      description: 'Advanced subscriber verifying TTS + glossary + Meeting AI all enabled.',
      tags: ['advanced', 'features', 'billing'],
      variables: {
        email: () => `e2e-advall-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced All Features User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (fresh)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Advanced',
        features: { tts: true, glossary: true, meetingAi: true },
        canStartSession: true,
      },
    },
    {
      id: 'advanced-hour-pack-pricing',
      name: 'Points Pack Pricing (tier-independent)',
      description: 'An Advanced subscriber reads the points-pack catalog: the same three packs and prices as every other tier and channel.',
      tags: ['advanced', 'hourpack', 'pricing', 'billing'],
      variables: {
        email: () => `e2e-advprice-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Advanced Pack Pricing User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'get-pricing',
          name: 'Verify Points Pack Prices',
          method: 'GET',
          path: '/billing/hour-packs',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', description: 'Points pack prices (tier-independent): 60 points $7.99, 200 points $24.99, 600 points $69.99. The list is empty while BILLING_USAGE_PACK_SALES_ENABLED is off.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category F: Business Plan — Meeting (6 scenarios)
// ---------------------------------------------------------------------------
const BUSINESS_CATEGORY = {
  id: 'business',
  name: 'Business Plan — Meeting',
  icon: 'Briefcase',
  description: 'Business meeting plan — Zoom: $550/mo, Web: $550/mo, 6,000 min/mo. 100 participants, 30 langs.',
  scenarios: [
    {
      id: 'biz-fresh',
      name: 'Fresh Business ($550/mo)',
      description: 'Business subscriber with full 6000 min included hours. Tests full Business dashboard.',
      tags: ['business', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-biz-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Test User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (6000 min included, 0 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 6000, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        planTier: 'Business',
        includedHoursRemaining: '6000 min',
        canStartSession: true,
        features: { tts: true, glossary: true, meetingAi: true },
      },
    },
    {
      id: 'biz-half-used',
      name: '50% Used',
      description: 'Business with 3000/6000 minutes used. Tests progress bar at 50%.',
      tags: ['business', 'subscription', 'partial', 'billing'],
      variables: {
        email: () => `e2e-bizhalf-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Half User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (3000/6000 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 6000, usedMinutes: 3000 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', includedHoursRemaining: '3000 min', canStartSession: true },
    },
    {
      id: 'biz-1min-left',
      name: '1 min Left',
      description: 'Business with 5999/6000 minutes used. Tests critical warning state.',
      tags: ['business', 'subscription', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-biznear-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Near Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (5999/6000 used)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 6000, usedMinutes: 5999 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', includedHoursRemaining: '1 min', canStartSession: true },
    },
    {
      id: 'biz-exhausted',
      name: 'Hours Exhausted',
      description: 'Business with 6000/6000 minutes used. Tests blocked state.',
      tags: ['business', 'subscription', 'exhausted', 'billing'],
      variables: {
        email: () => `e2e-bizout-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Exhausted User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (6000/6000 exhausted)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'zoom',
            status: 'active',
            includedHours: { totalMinutes: 6000, usedMinutes: 6000 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', includedHoursRemaining: '0 min', canStartSession: false },
    },
    {
      id: 'biz-30-languages',
      name: '30 Languages (at max)',
      description: 'Business plan at 30/30 language limit. Tests max language display.',
      tags: ['business', 'language-limit', 'billing'],
      variables: {
        email: () => `e2e-bizlang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Lang User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', maxLanguages: 30, description: '30 languages shown at limit.' },
    },
    {
      id: 'biz-100-participants',
      name: '100 Participants (at max)',
      description: 'Business plan at 100/100 participant limit. Tests max participant display.',
      tags: ['business', 'participant-limit', 'billing'],
      variables: {
        email: () => `e2e-bizpax-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Business Max Participants User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', maxParticipants: 100, description: '100 participants at max.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category G: Web Plans (6 scenarios)
// ---------------------------------------------------------------------------
const WEB_PLANS_CATEGORY = {
  id: 'web-plans',
  name: 'Web Plans',
  icon: 'Globe',
  description: 'Web subscription plans (direct/browser channel, not Zoom Marketplace).',
  scenarios: [
    {
      id: 'web-starter-fresh',
      name: 'Web Starter ($30/mo)',
      description: 'Web Starter subscriber with full included hours. Product type = web.',
      tags: ['web', 'starter', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-webstart-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Starter User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'web' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', productType: 'web', includedHoursRemaining: '60 min', canStartSession: true },
    },
    {
      id: 'web-standard-fresh',
      name: 'Web Standard ($45/mo)',
      description: 'Web Standard subscriber with full 60 min included hours. TTS enabled; Meeting AI funded by points.',
      tags: ['web', 'standard', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-webstd-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Standard User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'web' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min included)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 60, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', productType: 'web', includedHoursRemaining: '60 min', canStartSession: true },
    },
    {
      id: 'web-advanced-fresh',
      name: 'Web Advanced ($140/mo)',
      description: 'Web Advanced subscriber with full 1,200 min included hours. TTS + Meeting AI enabled.',
      tags: ['web', 'advanced', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-webadv-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Advanced User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'web' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (1200 min included)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 1200, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', productType: 'web', includedHoursRemaining: '1200 min', canStartSession: true },
    },
    {
      id: 'web-pro-fresh',
      name: 'Web Professional ($80/mo)',
      description: 'Web Professional subscriber with full included hours and all features.',
      tags: ['web', 'professional', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-webpro-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Pro User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'web' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (300 min included)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 300, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', productType: 'web', includedHoursRemaining: '300 min', canStartSession: true },
    },
    {
      id: 'web-biz-fresh',
      name: 'Web Business ($550/mo)',
      description: 'Web Business subscriber with full included hours.',
      tags: ['web', 'business', 'subscription', 'fresh', 'billing'],
      variables: {
        email: () => `e2e-webbiz-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Business User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'web' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (6000 min included)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            channel: 'direct',
            status: 'active',
            includedHours: { totalMinutes: 6000, usedMinutes: 0 },
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', productType: 'web', includedHoursRemaining: '6000 min', canStartSession: true },
    },
    {
      id: 'web-starter-glossary',
      name: 'Web Starter + Glossary',
      description: 'Web Starter subscriber with glossary access. Tests glossary available on web plans.',
      tags: ['web', 'starter', 'glossary', 'features', 'billing'],
      variables: {
        email: () => `e2e-webgloss-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Starter Glossary User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'web' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', productType: 'web', features: { glossary: true } },
    },
    {
      id: 'web-pro-all-features',
      name: 'Web Pro All Features',
      description: 'Web Professional with TTS + glossary + Meeting AI all enabled.',
      tags: ['web', 'professional', 'features', 'billing'],
      variables: {
        email: () => `e2e-webproall-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Pro All User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'web' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', productType: 'web', features: { tts: true, glossary: true, meetingAi: true } },
    },
    {
      id: 'web-biz-all-features',
      name: 'Web Business All Features',
      description: 'Web Business with full feature set: TTS, glossary, Meeting AI, 100 participants, 30 languages.',
      tags: ['web', 'business', 'features', 'billing'],
      variables: {
        email: () => `e2e-webbizall-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Web Business All User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business, web)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'web' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', productType: 'web', features: { tts: true, glossary: true, meetingAi: true } },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category H: Webinar Plans (8 scenarios)
// ---------------------------------------------------------------------------
const WEBINAR_PLANS_CATEGORY = {
  id: 'webinar-plans',
  name: 'Webinar Plans',
  icon: 'Video',
  description: 'Webinar plans — Zoom: $90-$1,250/mo, Web: $90-$1,250/mo. Q&A tiers, moderation, analytics.',
  scenarios: [
    {
      id: 'webinar-starter-fresh',
      name: 'Webinar Starter ($90/mo)',
      description: 'Webinar Starter subscriber. Q&A Basic tier: no moderation, no replies.',
      tags: ['webinar', 'starter', 'subscription', 'qa', 'billing'],
      variables: {
        email: () => `e2e-wbstart-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Starter User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', productType: 'webinar', features: { qaBasic: true, moderation: false } },
    },
    {
      id: 'webinar-standard-fresh',
      name: 'Webinar Standard ($200/mo)',
      description: 'Webinar Standard subscriber. Q&A Basic + TTS enabled. 6 hr/session max.',
      tags: ['webinar', 'standard', 'subscription', 'qa', 'billing'],
      variables: {
        email: () => `e2e-wbstd-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Standard User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', productType: 'webinar', features: { qaBasic: true, tts: true } },
    },
    {
      id: 'webinar-pro-fresh',
      name: 'Webinar Professional ($600/mo)',
      description: 'Webinar Professional subscriber. Q&A Pro tier: moderation + replies enabled.',
      tags: ['webinar', 'professional', 'subscription', 'qa', 'billing'],
      variables: {
        email: () => `e2e-wbpro-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Pro User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', productType: 'webinar', features: { moderation: true, replies: true } },
    },
    {
      id: 'webinar-biz-fresh',
      name: 'Webinar Business ($1,250/mo)',
      description: 'Webinar Business subscriber. Full Q&A + Meeting AI + analytics/export.',
      tags: ['webinar', 'business', 'subscription', 'qa', 'billing'],
      variables: {
        email: () => `e2e-wbbiz-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Business User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', productType: 'webinar', features: { meetingAi: true, analytics: true } },
    },
    {
      id: 'webinar-advanced-fresh',
      name: 'Webinar Advanced ($900/mo)',
      description: 'Webinar Advanced subscriber. Q&A Pro + full features. 12 hr/session max.',
      tags: ['webinar', 'advanced', 'subscription', 'qa', 'billing'],
      variables: {
        email: () => `e2e-wbadv-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Advanced User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Advanced, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Advanced', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Advanced', productType: 'webinar', features: { qaPro: true, meetingAi: true, tts: true } },
    },
    {
      id: 'webinar-starter-qa-basic',
      name: 'Q&A Basic: Limited Features',
      description: 'Webinar Starter with Q&A Basic: no moderation or reply features visible.',
      tags: ['webinar', 'starter', 'qa', 'features', 'billing'],
      variables: {
        email: () => `e2e-wbbasicqa-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar QA Basic User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', productType: 'webinar', description: 'Moderation and reply features should be hidden.' },
    },
    {
      id: 'webinar-pro-qa-moderation',
      name: 'Q&A Pro: Moderation Active',
      description: 'Webinar Professional with moderation queue visible.',
      tags: ['webinar', 'professional', 'qa', 'moderation', 'billing'],
      variables: {
        email: () => `e2e-wbmod-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Moderation User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', productType: 'webinar', features: { moderation: true }, description: 'Moderation queue visible.' },
    },
    {
      id: 'webinar-pro-qa-replies',
      name: 'Q&A Pro: Replies Enabled',
      description: 'Webinar Professional with reply-to-questions feature enabled.',
      tags: ['webinar', 'professional', 'qa', 'replies', 'billing'],
      variables: {
        email: () => `e2e-wbreply-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Replies User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', productType: 'webinar', features: { replies: true } },
    },
    {
      id: 'webinar-biz-qa-analytics',
      name: 'Q&A Pro: Analytics/Export',
      description: 'Webinar Business with CSV export and analytics features.',
      tags: ['webinar', 'business', 'qa', 'analytics', 'billing'],
      variables: {
        email: () => `e2e-wbanal-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar Analytics User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Business, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Business', productType: 'webinar', features: { analytics: true, export: true } },
    },
    {
      id: 'webinar-qa-limit-reached',
      name: 'Q&A: Max Questions Hit',
      description: 'Webinar Starter at the 100-question limit (100/100). Tests the at-limit state.',
      tags: ['webinar', 'starter', 'qa', 'limit', 'billing'],
      variables: {
        email: () => `e2e-wblimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Webinar QA Limit User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', productType: 'webinar', description: '100/100 questions shown. New questions blocked.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category I: Event Passes (14 scenarios)
// ---------------------------------------------------------------------------
const EVENT_PASSES_CATEGORY = {
  id: 'event-passes',
  name: 'Event Passes',
  icon: 'Ticket',
  description: 'EventPass flat-rate webinar passes: 6 tiers, in-use, used, expired states.',
  scenarios: [
    {
      id: 'pass-starter-available',
      name: 'Starter Pass ($99, 4hr/10att)',
      description: 'Starter EventPass available for use. Tests pass shown in billing with correct limits.',
      tags: ['eventpass', 'starter', 'billing-priority'],
      variables: {
        email: () => `e2e-pstart-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Starter User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Starter, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Starter', passStatus: 'Available', passPrice: '$99', maxAttendees: 10, maxDuration: '4 hr' },
    },
    {
      id: 'pass-pro-available',
      name: 'Professional Pass ($199)',
      description: 'Professional EventPass available. Tests higher limits: 25 attendees, 3 languages.',
      tags: ['eventpass', 'professional', 'billing-priority'],
      variables: {
        email: () => `e2e-ppro-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Pro User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Professional, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Professional',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 25,
            maxLanguages: 3,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Professional', passStatus: 'Available', passPrice: '$199', maxAttendees: 25 },
    },
    {
      id: 'pass-biz-available',
      name: 'Business Pass ($499)',
      description: 'Business EventPass available. Tests 50 attendees, 5 languages.',
      tags: ['eventpass', 'business', 'billing-priority'],
      variables: {
        email: () => `e2e-pbiz-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Business User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Business, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Business',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 50,
            maxLanguages: 5,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Business', passStatus: 'Available', passPrice: '$499', maxAttendees: 50 },
    },
    {
      id: 'pass-ent100-available',
      name: 'Enterprise100 Pass ($799)',
      description: 'Enterprise100 EventPass. 100 attendees, 8hr duration, 60+ languages.',
      tags: ['eventpass', 'enterprise', 'billing-priority'],
      variables: {
        email: () => `e2e-pent100-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Ent100 User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Enterprise100, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Enterprise100',
            status: 'Available',
            maxDurationMinutes: 480,
            maxAttendees: 100,
            maxLanguages: 60,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Enterprise100', passPrice: '$799', maxAttendees: 100, maxDuration: '8 hr' },
    },
    {
      id: 'pass-ent300-available',
      name: 'Enterprise300 Pass ($999)',
      description: 'Enterprise300 EventPass. 300 attendees, 8hr duration.',
      tags: ['eventpass', 'enterprise', 'billing-priority'],
      variables: {
        email: () => `e2e-pent300-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Ent300 User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Enterprise300, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Enterprise300',
            status: 'Available',
            maxDurationMinutes: 480,
            maxAttendees: 300,
            maxLanguages: 60,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Enterprise300', passPrice: '$999', maxAttendees: 300 },
    },
    {
      id: 'pass-ent500-available',
      name: 'Enterprise500 Pass ($1199)',
      description: 'Enterprise500 EventPass. 500 attendees, 8hr duration — top tier.',
      tags: ['eventpass', 'enterprise', 'billing-priority'],
      variables: {
        email: () => `e2e-pent500-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Ent500 User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Enterprise500, Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Enterprise500',
            status: 'Available',
            maxDurationMinutes: 480,
            maxAttendees: 500,
            maxLanguages: 60,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passType: 'Enterprise500', passPrice: '$1199', maxAttendees: 500 },
    },
    {
      id: 'pass-in-use',
      name: 'Pass Currently In Use',
      description: 'Pass with status InUse and a sessionId set. Tests the "In Use" badge display.',
      tags: ['eventpass', 'in-use', 'billing-priority'],
      variables: {
        email: () => `e2e-pinuse-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass In Use User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Starter, InUse)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'InUse',
            sessionId: '{sessionId}',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'InUse', description: '"In Use" badge shown. Pass locked to session.' },
    },
    {
      id: 'pass-used-consumed',
      name: 'Pass Already Consumed',
      description: 'Pass with status Used. Tests greyed-out display and non-selectability.',
      tags: ['eventpass', 'used', 'billing-priority'],
      variables: {
        email: () => `e2e-pused-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Used User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Starter, Used)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'Used',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            usedAt: new Date().toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'Used', description: 'Pass shown greyed out. Cannot be selected for new session.' },
    },
    {
      id: 'pass-expired',
      name: 'Pass Expired (>12 months)',
      description: 'Pass with expiresAt in the past. Tests the "Expired" badge display.',
      tags: ['eventpass', 'expired', 'billing-priority'],
      variables: {
        email: () => `e2e-pexp-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Expired User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Starter, Available, expired date)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() - 400 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'Expired', description: '"Expired" badge shown. Pass not usable.' },
    },
    {
      id: 'pass-near-duration-limit',
      name: 'Pass: 1 min From Max Duration',
      description: 'Pass in use with 239/240 minutes elapsed. Tests time warning display.',
      tags: ['eventpass', 'near-limit', 'billing-priority'],
      variables: {
        email: () => `e2e-ptimelimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Near Duration User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (InUse, 239/240 min elapsed)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'InUse',
            sessionId: '{sessionId}',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'InUse', description: 'Time warning visible: 1 minute remaining of 4hr limit.' },
    },
    {
      id: 'pass-near-attendee-limit',
      name: 'Pass: 1 Below Max Attendees',
      description: 'Pass in use with 9/10 attendees. Tests near-limit attendee indicator.',
      tags: ['eventpass', 'near-limit', 'attendee-limit', 'billing-priority'],
      variables: {
        email: () => `e2e-pattlimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Near Attendee User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (InUse, 9/10 attendees)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'InUse',
            sessionId: '{sessionId}',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'InUse', description: '9/10 attendees. Near-limit indicator visible.' },
    },
    {
      id: 'pass-at-attendee-max',
      name: 'Pass: At Max Attendees',
      description: 'Pass in use with 10/10 attendees at limit. No more joins allowed.',
      tags: ['eventpass', 'limit', 'attendee-limit', 'billing-priority'],
      variables: {
        email: () => `e2e-patmax-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass At Max User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (InUse, 10/10 attendees)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'InUse',
            sessionId: '{sessionId}',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'InUse', description: '10/10 attendees at max. Join button disabled.' },
    },
    {
      id: 'pass-over-attendee-max',
      name: 'Pass: Over Max (auto-upgrade)',
      description: 'Pass with 11 attendees exceeding the 10-person limit. Tests auto-upgrade trigger.',
      tags: ['eventpass', 'over-limit', 'auto-upgrade', 'billing-priority'],
      variables: {
        email: () => `e2e-pover-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Over Max User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (InUse, over limit)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'InUse',
            sessionId: '{sessionId}',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { passStatus: 'InUse', description: 'Auto-upgrade prompt shown when attendee count exceeds pass limit.' },
    },
    {
      id: 'pass-multiple',
      name: 'Multiple Passes Available',
      description: 'Two passes available (Starter + Professional). System picks best match.',
      tags: ['eventpass', 'multiple', 'billing-priority'],
      variables: {
        email: () => `e2e-pmulti-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pass Multiple User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-eventpass-1',
          name: 'Seed First EventPass (Starter)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
        {
          id: 'seed-eventpass-2',
          name: 'Seed Second EventPass (Professional)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Professional',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 25,
            maxLanguages: 3,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Two passes listed. System selects best-fit based on session requirements.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category K: Billing Priority Chain (4 scenarios)
// ---------------------------------------------------------------------------
const BILLING_PRIORITY_CATEGORY = {
  id: 'billing-priority',
  name: 'Billing Priority Chain',
  icon: 'ArrowUpDown',
  description: 'Spend order: SuperAdmin bypass > EventPass (broadcast only) > monthly subscription points > purchased points > refused with usage_balance_insufficient and the buy_pack remedy. No other source exists; a broadcast shortfall is an event-pass or upgrade remedy, never points.',
  scenarios: [
    {
      id: 'priority-superadmin',
      name: 'SuperAdmin Bypass',
      description: 'Internal c2hl tenant with SuperAdmin bypass. All features, no billing limits applied.',
      tags: ['priority', 'superadmin', 'billing-priority'],
      variables: userVariables('sa', 'SuperAdmin User'),
      steps: [
        registerStep(),
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (SuperAdmin bypass)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Unlimited', productType: 'all' },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        description: 'SuperAdmin/Unlimited tenant: all features enabled, no billing chain evaluated.',
      },
    },
    {
      id: 'priority-eventpass-first',
      name: 'EventPass Over Everything',
      description: 'EventPass available beside a Meeting subscription with monthly points. A broadcast draws the EventPass first and never hands off to Meeting points.',
      tags: ['priority', 'eventpass', 'billing-priority'],
      variables: userVariables('priepass', 'EventPass Priority User'),
      steps: [
        registerStep(),
        {
          id: 'seed-eventpass',
          name: 'Seed EventPass (Available)',
          method: 'POST',
          path: '/test/seed/eventpass',
          auth: 'function-key',
          body: {
            tenantId: '{tenantId}',
            tier: 'Starter',
            status: 'Available',
            maxDurationMinutes: 240,
            maxAttendees: 10,
            maxLanguages: 1,
            expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          },
          expect: { status: [200] },
        },
        seedSubscriptionStep('plan_meeting_pro_v2', 'Seed Subscription (Meeting, monthly points)'),
        entitlementStep(),
      ],
      expectedState: {
        description: 'The EventPass is the broadcast funding source; Meeting points are untouched by a broadcast.',
        broadcastFallsBackToPoints: false,
      },
    },
    {
      id: 'priority-monthly-over-purchased',
      name: 'Monthly Points Before Purchased Points',
      description: 'A Meeting subscriber spends the monthly grant first; purchased points are spent only after the monthly points run out. This scenario starts at the fresh monthly grant and checks that the monthly meter leads.',
      tags: ['priority', 'monthly-points', 'purchased-points', 'billing-priority'],
      variables: userVariables('primonthly', 'Monthly First User'),
      steps: [
        registerStep(),
        seedSubscriptionStep('plan_meeting_pro_v2', 'Seed Subscription (Meeting, fresh monthly grant)'),
        entitlementStep(),
      ],
      expectedState: {
        description: 'usageCredits.monthlyMilli leads and is "in use"; purchased points stay untouched until it reaches 0.',
        monthlyPoints: 200,
      },
    },
    {
      id: 'priority-all-exhausted',
      name: 'Everything Empty',
      description: 'No EventPass, no subscription, no purchased points. Completely blocked with the points-pack remedy.',
      tags: ['priority', 'blocked', 'exhausted', 'billing-priority'],
      variables: userVariables('priexh', 'All Exhausted User'),
      steps: [registerStep(), entitlementStep()],
      expectedState: {
        canStartSession: false,
        refusal: 'usage_balance_insufficient with options [buy_pack]',
        description: 'Completely blocked. Buy-points CTA shown prominently.',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category L: Session Lifecycle (6 scenarios)
// ---------------------------------------------------------------------------
const SESSION_LIFECYCLE_CATEGORY = {
  id: 'session-lifecycle',
  name: 'Session Lifecycle',
  icon: 'Monitor',
  description: 'Session create, start, pause, resume, end, and force-terminate flows.',
  scenarios: [
    {
      id: 'session-create-verify',
      name: 'Create + Verify',
      description: 'Seed a tenant, create a session, and verify it appears in the dashboard.',
      tags: ['session', 'lifecycle', 'create'],
      variables: {
        email: () => `e2e-sess-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session Create User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-host',
          name: 'Register Session Host',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          // hostId was missing here — register-host has always required it, so this step
          // 400'd and the scenario still read as green. Ownership fields are required too
          // (plan §3.2 / S0).
          body: {
            tenantId: '{tenantId}',
            sessionId: '{sessionId}',
            hostId: 'host-{sessionId}',
            metadata: { source: 'integration-test' },
            testRunId: '{sessionId}',
          },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { description: 'Session appears in dashboard with "active" status.' },
    },
    {
      id: 'session-start-billing',
      name: 'Start Billing',
      description: 'Create session then verify the billing source is displayed correctly.',
      tags: ['session', 'lifecycle', 'billing'],
      variables: {
        email: () => `e2e-sessbill-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session Billing User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (fresh)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'register-host',
          name: 'Register Session Host',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          // hostId was missing here — register-host has always required it, so this step
          // 400'd and the scenario still read as green. Ownership fields are required too
          // (plan §3.2 / S0).
          body: {
            tenantId: '{tenantId}',
            sessionId: '{sessionId}',
            hostId: 'host-{sessionId}',
            metadata: { source: 'integration-test' },
            testRunId: '{sessionId}',
          },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { description: 'Billing source displayed: IncludedHours. Timer counting up.' },
    },
    {
      id: 'session-pause-resume',
      name: 'Pause + Resume',
      description: 'Session that supports pause/resume flow. Tests pause and resume controls.',
      tags: ['session', 'lifecycle', 'pause', 'resume'],
      variables: {
        email: () => `e2e-sesspause-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session Pause User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (60 min)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'register-host',
          name: 'Register Session Host',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          // hostId was missing here — register-host has always required it, so this step
          // 400'd and the scenario still read as green. Ownership fields are required too
          // (plan §3.2 / S0).
          body: {
            tenantId: '{tenantId}',
            sessionId: '{sessionId}',
            hostId: 'host-{sessionId}',
            metadata: { source: 'integration-test' },
            testRunId: '{sessionId}',
          },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { description: 'Pause button visible. After pause: billing stops. After resume: billing resumes.' },
    },
    {
      id: 'session-normal-end',
      name: 'Normal Session End',
      description: 'Session created and ended normally. Tests final charge calculation display.',
      tags: ['session', 'lifecycle', 'end', 'billing'],
      variables: {
        email: () => `e2e-sessend-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session End User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'register-host',
          name: 'Register Session Host',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          // hostId was missing here — register-host has always required it, so this step
          // 400'd and the scenario still read as green. Ownership fields are required too
          // (plan §3.2 / S0).
          body: {
            tenantId: '{tenantId}',
            sessionId: '{sessionId}',
            hostId: 'host-{sessionId}',
            metadata: { source: 'integration-test' },
            testRunId: '{sessionId}',
          },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { description: 'After ending session: final charge shown, usage history updated.' },
    },
    {
      id: 'session-time-warning',
      name: 'Time Limit Warning',
      description: 'Session created with only 1 minute of included hours left. Tests the near-limit warning banner during a session.',
      tags: ['session', 'lifecycle', 'near-limit', 'billing'],
      variables: {
        email: () => `e2e-sesstimewarn-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session Time Warning User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Subscription (1 min left)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 59 } },
          expect: { status: [200] },
        },
        {
          id: 'register-host',
          name: 'Register Session Host',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          // hostId was missing here — register-host has always required it, so this step
          // 400'd and the scenario still read as green. Ownership fields are required too
          // (plan §3.2 / S0).
          body: {
            tenantId: '{tenantId}',
            sessionId: '{sessionId}',
            hostId: 'host-{sessionId}',
            metadata: { source: 'integration-test' },
            testRunId: '{sessionId}',
          },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { description: 'Warning banner: "1 minute remaining." Session terminates when limit hit.' },
    },
    {
      id: 'session-force-terminate',
      name: 'Force Terminate (no balance)',
      description: 'Session with all billing sources exhausted mid-session. Tests the force-terminate state.',
      tags: ['session', 'lifecycle', 'force-terminate', 'billing'],
      variables: {
        email: () => `e2e-sessterm-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Session Terminate User',
        sessionId: () => `e2e-sess-${Date.now()}`,
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        entitlementStep(),
      ],
      expectedState: { canStartSession: false, usageCredits: 0, description: 'Session terminated state: no subscription points and no purchased points, so the shortfall is blocked.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category M: Plan Limit Enforcement (9 scenarios)
// ---------------------------------------------------------------------------
const PLAN_LIMITS_CATEGORY = {
  id: 'plan-limits',
  name: 'Plan Limit Enforcement',
  icon: 'Shield',
  description: 'Tests plan-level feature and quantity limits (languages, participants, TTS, glossary) and that Meeting AI stays points-funded on every plan.',
  scenarios: [
    {
      id: 'limit-free-1-language',
      name: 'Free: Max 1 Language',
      description: 'Free tier user. Tests that only 1 target language is allowed (maxTargetLanguages=1).',
      tags: ['limits', 'free', 'language-limit'],
      variables: {
        email: () => `e2e-lim1lang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Free 1 Language User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { planTier: 'Free', maxLanguages: 1, description: 'Adding a 2nd language should be blocked.' },
    },
    {
      id: 'limit-free-3-participants',
      name: 'Free: Max 3 Participants',
      description: 'Free tier user. Tests that only 3 participants are allowed (maxParticipants=3).',
      tags: ['limits', 'free', 'participant-limit'],
      variables: {
        email: () => `e2e-lim3pax-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Free 3 Participants User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { planTier: 'Free', maxParticipants: 3, description: '4th participant join blocked.' },
    },
    {
      id: 'limit-free-no-tts',
      name: 'Free: No TTS',
      description: 'Free tier user. Tests that TTS toggle is hidden or disabled (ttsEnabled=false).',
      tags: ['limits', 'free', 'tts'],
      variables: {
        email: () => `e2e-limnotts-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Free No TTS User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { planTier: 'Free', features: { tts: false }, description: 'TTS toggle hidden/disabled.' },
    },
    {
      id: 'limit-free-no-glossary',
      name: 'Free: No Glossary',
      description: 'Free tier user. Tests that Glossary tab is hidden (glossaryEnabled=false).',
      tags: ['limits', 'free', 'glossary'],
      variables: {
        email: () => `e2e-limnogloss-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Free No Glossary User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { planTier: 'Free', features: { glossary: false }, description: 'Glossary tab hidden.' },
    },
    {
      id: 'limit-free-meetingai-points',
      name: 'Free: Meeting AI Funded by Points',
      description: 'Free tier user. Meeting AI has no plan flag: the AI tab is shown on Free, points fund each call, and an unfunded call is refused with the 402 shortage (options: ["buy_pack"]). Incident 2026-11-04.',
      tags: ['limits', 'free', 'meetingai', 'points'],
      variables: {
        email: () => `e2e-limfreeai-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Free Meeting AI User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
      ],
      expectedState: { planTier: 'Free', features: { meetingAi: true }, description: 'Meeting AI tab shown; a call without enough points gets 402 buy_pack, never a plan lock.' },
    },
    {
      id: 'limit-starter-no-tts',
      name: 'Starter: No TTS',
      description: 'Starter subscriber. Tests that TTS is not available at the Starter tier.',
      tags: ['limits', 'starter', 'tts'],
      variables: {
        email: () => `e2e-startnotts-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter No TTS User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', features: { tts: false }, description: 'TTS not available on Starter.' },
    },
    {
      id: 'limit-starter-1-language',
      name: 'Starter: Max 1 Language',
      description: 'Starter subscriber. Tests the 1-language limit enforcement.',
      tags: ['limits', 'starter', 'language-limit'],
      variables: {
        email: () => `e2e-start1lang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Starter 1 Language User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Starter', maxLanguages: 1, description: '2nd language blocked.' },
    },
    {
      id: 'limit-standard-3-languages',
      name: 'Standard: Max 3 Languages',
      description: 'Standard subscriber. Tests the 3-language limit (4th language blocked).',
      tags: ['limits', 'standard', 'language-limit'],
      variables: {
        email: () => `e2e-std3lang-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Standard 3 Language User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Standard)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Standard', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Standard', maxLanguages: 3, description: '4th language blocked.' },
    },
    {
      id: 'limit-pro-meetingai',
      name: 'Pro: MeetingAI Enabled',
      description: 'Professional subscriber. Tests that Meeting AI tab is available and functional.',
      tags: ['limits', 'professional', 'meetingai'],
      variables: {
        email: () => `e2e-proai-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Pro MeetingAI User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Professional', features: { meetingAi: true }, description: 'Meeting AI tab shown and enabled.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category N: Cleanup & Utilities (3 scenarios)
// ---------------------------------------------------------------------------
const CLEANUP_CATEGORY = {
  id: 'cleanup',
  name: 'Cleanup & Utilities',
  icon: 'Trash2',
  description: 'Delete test tenants and verify API health.',
  scenarios: [
    {
      id: 'cleanup-single',
      name: 'Delete Single Test Tenant',
      description: 'Delete a specific test tenant by tenantId. Use after manual testing to clean up.',
      tags: ['cleanup', 'utility'],
      variables: {
        tenantId: 'enter-tenant-id-here',
      },
      steps: [
        {
          id: 'delete-tenant',
          name: 'Delete Test Tenant',
          method: 'DELETE',
          path: '/test/seed/tenant/{tenantId}',
          auth: 'jwt',
          expect: { status: [200, 204] },
        },
      ],
      expectedState: { description: 'Tenant and all associated data deleted.' },
    },
    {
      id: 'cleanup-all',
      name: 'Delete All Saved Test Tenants',
      description: 'Iterate through all credentials saved in the vault and delete each tenant.',
      tags: ['cleanup', 'utility', 'bulk'],
      variables: {},
      steps: [
        {
          id: 'health-check',
          name: 'Verify API Health Before Cleanup',
          method: 'GET',
          path: '/health',
          auth: 'none',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Use "Cleanup All Test Data" button in top bar for bulk deletion.' },
    },
    {
      id: 'util-health-check',
      name: 'Health Check',
      description: 'Simple GET /health to verify the Functions API is reachable and responding.',
      tags: ['utility', 'health'],
      variables: {},
      steps: [
        {
          id: 'health',
          name: 'GET /health',
          method: 'GET',
          path: '/health',
          auth: 'none',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'API responds with 200 OK. Functions host is running.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category Q: Monthly & Purchased Points (3 scenarios)
// ---------------------------------------------------------------------------
const INCLUDED_PURCHASED_CATEGORY = {
  id: 'included-purchased',
  name: 'Monthly & Purchased Points',
  icon: 'CreditCard',
  description: 'Monthly subscription points (Meeting 200, Webinar 300) are granted each period and expire at the period reset; purchased points never expire and are spent after the monthly points.',
  scenarios: [
    {
      id: 'included-fresh',
      name: 'Meeting Subscription — 200 Monthly Points',
      description: 'A Meeting subscription receives its monthly grant of 200 points. The first entitlement read creates the grant.',
      tags: ['monthly-points', 'subscription', 'fresh', 'points'],
      variables: userVariables('incfresh', 'Monthly Fresh User'),
      steps: [
        registerStep(),
        seedSubscriptionStep('plan_meeting_pro_v2', 'Seed Subscription (Meeting)'),
        entitlementStep(),
      ],
      expectedState: { monthlyPoints: 200, canStartSession: true },
    },
    {
      id: 'included-webinar-grant',
      name: 'Webinar Subscription — 300 Monthly Points',
      description: 'Every sold Webinar tier receives 300 monthly points (owner decision 6), replacing the Meeting allowance the retired legacy union gave it.',
      tags: ['monthly-points', 'subscription', 'webinar', 'points'],
      variables: userVariables('incweb', 'Webinar Grant User'),
      steps: [
        registerStep(),
        seedSubscriptionStep('plan_webinar_v2', 'Seed Subscription (Webinar)'),
        entitlementStep(),
      ],
      expectedState: { monthlyPoints: 300, canStartSession: true },
    },
    {
      id: 'included-no-subscription',
      name: 'No Subscription — No Monthly Grant',
      description: 'Without a subscription there is no monthly grant. Purchased points are the only balance, and at zero the tenant is refused with the points-pack remedy.',
      tags: ['monthly-points', 'no-subscription', 'points'],
      variables: userVariables('incnone', 'No Grant User'),
      steps: [registerStep(), entitlementStep()],
      expectedState: {
        monthlyPoints: 0,
        availablePoints: 0,
        canStartSession: false,
        refusal: 'usage_balance_insufficient with options [buy_pack]',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category X: PricingCatalog & Dynamic Pricing (6 scenarios)
// ---------------------------------------------------------------------------
const PRICING_CATALOG_CATEGORY = {
  id: 'pricing-catalog',
  name: 'PricingCatalog & Dynamic Pricing',
  icon: 'RefreshCw',
  description: 'Runtime pricing catalog — read/update prices without redeployment. Requires SuperAdmin JWT.',
  scenarios: [
    {
      id: 'catalog-read-default',
      name: 'Read Default Catalog',
      description: 'GET /api/admin/pricing-catalog — returns seeded defaults from BillingConstants.',
      tags: ['pricing-catalog', 'admin', 'billing'],
      variables: {
        email: () => `e2e-catread-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Catalog Read User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'read-catalog',
          name: 'Read Pricing Catalog',
          method: 'GET',
          path: '/platform/pricing',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Returns catalog with default prices from BillingConstants.' },
    },
    {
      id: 'catalog-update-eventpass',
      name: 'Update EventPass Price',
      description: 'POST /api/admin/pricing-catalog — modify EventPass base price.',
      tags: ['pricing-catalog', 'admin', 'eventpass', 'billing'],
      variables: {
        email: () => `e2e-catepass-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Catalog EventPass User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'update-catalog',
          name: 'Update EventPass Starter Price',
          method: 'PUT',
          path: '/platform/pricing',
          auth: 'jwt',
          body: { eventPasses: { starter: { basePriceCents: 8900 } } },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Starter EventPass now $89. Applied to next EventPass purchase.' },
    },
    {
      id: 'catalog-update-perminute',
      name: 'Update Per-Minute Rate',
      description: 'POST /api/admin/pricing-catalog — modify the per-minute billing rate.',
      tags: ['pricing-catalog', 'admin', 'per-minute', 'billing'],
      variables: {
        email: () => `e2e-catpmin-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Catalog Per-Minute User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'update-catalog',
          name: 'Update Per-Minute Rate',
          method: 'PUT',
          path: '/platform/pricing',
          auth: 'jwt',
          body: { perMinuteRates: { free: 35 } },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Per-minute rate updated. New rate used in next heartbeat billing.' },
    },
    {
      id: 'catalog-update-freetier',
      name: 'Update Free Tier Minutes',
      description: 'POST /api/admin/pricing-catalog — modify the daily free tier allowance.',
      tags: ['pricing-catalog', 'admin', 'free-tier', 'billing'],
      variables: {
        email: () => `e2e-catft-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Catalog Free Tier User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'update-catalog',
          name: 'Update Per-Minute Rate',
          method: 'PUT',
          path: '/platform/pricing',
          auth: 'jwt',
          body: { perMinuteRates: { Free: 0 } },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Per-minute rate updated in the pricing catalog and returned by the next GET.' },
    },
    {
      id: 'catalog-auth-required',
      name: 'Catalog Requires SuperAdmin',
      description: 'Call GET /api/admin/pricing-catalog without JWT. Expects 401 Unauthorized.',
      tags: ['pricing-catalog', 'auth', 'security'],
      variables: {},
      steps: [
        {
          id: 'read-catalog-no-auth',
          name: 'Read Pricing Catalog (no auth)',
          method: 'GET',
          path: '/platform/pricing',
          auth: 'none',
          expect: { status: [401, 403] },
        },
      ],
      expectedState: { description: '401 or 403 returned. Catalog inaccessible without SuperAdmin JWT.' },
    },
    {
      id: 'catalog-seed-on-first-read',
      name: 'Catalog Auto-Seeds',
      description: 'GET /api/admin/pricing-catalog when no catalog document exists in Cosmos. Auto-seeds from BillingConstants defaults.',
      tags: ['pricing-catalog', 'admin', 'seed', 'billing'],
      variables: {
        email: () => `e2e-catseed-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Catalog Auto Seed User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'read-catalog',
          name: 'Read Pricing Catalog (first time)',
          method: 'GET',
          path: '/platform/pricing',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'If no catalog exists, auto-creates from BillingConstants. Returns 200 with defaults.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category Y: Product Type Gates (6 scenarios)
// ---------------------------------------------------------------------------
const PRODUCT_TYPE_GATES_CATEGORY = {
  id: 'product-type-gates',
  name: 'Product Type Gates',
  icon: 'CheckCircle',
  description: 'Meeting vs webinar product gates: Hour Packs (meeting-only), EventPasses (webinar-only).',
  scenarios: [
    {
      id: 'gate-hourpack-meeting-ok',
      name: 'Points Pack Purchase (Meeting Plan)',
      description: 'Subscriber on meeting plan attempts a points pack purchase. Should succeed.',
      tags: ['product-gates', 'hour-pack', 'meeting', 'billing'],
      variables: {
        email: () => `e2e-gatemeetok-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate Meeting OK User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, meeting)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'seed-subscription',
          name: 'Seed Active Subscription',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'purchase-hourpack',
          name: 'Purchase Points Pack (expect success)',
          method: 'POST',
          path: '/billing/pack/purchase',
          auth: 'jwt',
          body: { productId: 'ai.qolsync.pack.usage.60', successUrl: 'https://qolsync.ai/billing/success', cancelUrl: 'https://qolsync.ai/billing/cancel' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Points pack purchase succeeds for a meeting plan subscriber (same 60 points $7.99 SKU as iOS). Requires BILLING_USAGE_PACK_SALES_ENABLED; a closed storefront answers 400 UNKNOWN_PACK.' },
    },
    {
      id: 'gate-hourpack-webinar-blocked',
      name: 'Hour Pack Blocked (Webinar Plan)',
      description: 'Subscriber on webinar plan attempts Hour Pack purchase. Should return 403 MEETING_SUBSCRIPTION_REQUIRED.',
      tags: ['product-gates', 'hour-pack', 'webinar', 'blocked', 'billing'],
      variables: {
        email: () => `e2e-gatewebblk-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate Webinar Blocked User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'purchase-hourpack-blocked',
          name: 'Purchase Points Pack (D30/D31: no plan or subscription gate — expect 200)',
          method: 'POST',
          path: '/billing/pack/purchase',
          auth: 'jwt',
          body: { productId: 'ai.qolsync.pack.usage.60', successUrl: 'https://qolsync.ai/billing/success', cancelUrl: 'https://qolsync.ai/billing/cancel' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Checkout URL returned: points packs are channel- and plan-neutral (D30), so a webinar subscriber may buy one too.' },
    },
    {
      id: 'gate-eventpass-webinar-ok',
      name: 'EventPass Purchase (Webinar Plan)',
      description: 'Subscriber on webinar plan purchases EventPass. Should succeed.',
      tags: ['product-gates', 'eventpass', 'webinar', 'billing'],
      variables: {
        email: () => `e2e-gatewebpass-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate Webinar Pass User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'purchase-eventpass',
          name: 'Purchase EventPass Starter (expect success)',
          method: 'POST',
          path: '/billing/event-pass/purchase',
          auth: 'jwt',
          body: { tier: 'Starter' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'EventPass purchase succeeds for webinar plan subscriber.' },
    },
    {
      id: 'gate-eventpass-meeting-blocked',
      name: 'EventPass Blocked (Meeting Plan)',
      description: 'Subscriber on meeting plan attempts EventPass purchase. Should fail (meeting plans do not support EventPass).',
      tags: ['product-gates', 'eventpass', 'meeting', 'blocked', 'billing'],
      variables: {
        email: () => `e2e-gatemeetpassblk-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate Meeting Pass Blocked User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, meeting)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'purchase-eventpass-blocked',
          name: 'Purchase EventPass (expect error)',
          method: 'POST',
          path: '/billing/event-pass/purchase',
          auth: 'jwt',
          body: { tier: 'Starter' },
          expect: { status: [400, 403, 422] },
        },
      ],
      expectedState: { description: 'Error returned. Meeting plans cannot purchase EventPasses.' },
    },
    {
      id: 'gate-unlimited-both-ok',
      name: 'Unlimited Plan (Both Allowed)',
      description: 'Unlimited/Platform plan (productType="all"). Both Hour Packs and EventPasses are purchasable.',
      tags: ['product-gates', 'unlimited', 'billing'],
      variables: {
        email: () => `e2e-gateunlimited-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate Unlimited User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Unlimited, all)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Unlimited', productType: 'all' },
          expect: { status: [200] },
        },
      ],
      expectedState: { planTier: 'Unlimited', description: 'Both Hour Packs and EventPasses purchasable on Unlimited plan.' },
    },
    {
      id: 'gate-hourpack-no-sub',
      name: 'Hour Pack Blocked (No Subscription)',
      description: 'Non-subscriber attempts Hour Pack purchase. Should return 403 SUBSCRIPTION_REQUIRED.',
      tags: ['product-gates', 'hour-pack', 'no-subscription', 'blocked', 'billing'],
      variables: {
        email: () => `e2e-gatenosub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Gate No Sub User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (free tier)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'purchase-hourpack-blocked',
          name: 'Purchase Points Pack (D30/D31: no plan or subscription gate — expect 200)',
          method: 'POST',
          path: '/billing/pack/purchase',
          auth: 'jwt',
          body: { productId: 'ai.qolsync.pack.usage.60', successUrl: 'https://qolsync.ai/billing/success', cancelUrl: 'https://qolsync.ai/billing/cancel' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Checkout URL returned: no subscription is required to buy a points pack (D31); the gate sizes the session from the pack alone.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category Z: EventPass Tier-Discounted Pricing (7 scenarios)
// ---------------------------------------------------------------------------
const EVENTPASS_DISCOUNTS_CATEGORY = {
  id: 'eventpass-discounts',
  name: 'EventPass Tier Discounts',
  icon: 'Tag',
  description: 'Subscriber discounts on EventPass: 1-jump=40% off, 2-jump=25% off, 3-jump=10% off.',
  scenarios: [
    {
      id: 'epdiscount-1jump',
      name: '1-Tier Jump (40% off)',
      description: 'Starter subscriber purchasing Professional EventPass (1-tier jump). Verifies 40% discount applied to base price.',
      tags: ['eventpass-discount', 'discount', 'billing'],
      variables: {
        email: () => `e2e-epd1j-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Discount 1 Jump User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'get-discount-price',
          name: 'Get Discounted EventPass Price (Professional tier)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Professional',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Professional EventPass price shows 40% discount for Starter subscriber (1-tier jump).' },
    },
    {
      id: 'epdiscount-2jump',
      name: '2-Tier Jump (25% off)',
      description: 'Starter subscriber purchasing Advanced EventPass (2-tier jump). Verifies 25% discount.',
      tags: ['eventpass-discount', 'discount', 'billing'],
      variables: {
        email: () => `e2e-epd2j-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Discount 2 Jump User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'get-discount-price',
          name: 'Get Discounted EventPass Price (Advanced tier)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Advanced',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Advanced EventPass price shows 25% discount for Starter subscriber (2-tier jump).' },
    },
    {
      id: 'epdiscount-3jump',
      name: '3-Tier Jump (10% off)',
      description: 'Starter subscriber purchasing Business EventPass (3-tier jump). Verifies 10% discount (minimum).',
      tags: ['eventpass-discount', 'discount', 'billing'],
      variables: {
        email: () => `e2e-epd3j-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Discount 3 Jump User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'get-discount-price',
          name: 'Get Discounted EventPass Price (Business tier)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Business',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Business EventPass price shows 10% discount for Starter subscriber (3-tier jump).' },
    },
    {
      id: 'epdiscount-no-sub',
      name: 'No Subscription (Full Price)',
      description: 'Non-subscriber purchasing EventPass. No discount applied — full base price.',
      tags: ['eventpass-discount', 'no-subscription', 'billing'],
      variables: {
        email: () => `e2e-epdnosub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP No Sub User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (free tier)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'get-price',
          name: 'Get EventPass Price (no subscription)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Professional',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Full base price returned. No subscription = no discount.' },
    },
    {
      id: 'epdiscount-same-tier',
      name: 'Same Tier (No EventPass)',
      description: 'Professional subscriber looking at Professional EventPass. Same-tier = no discount applicable.',
      tags: ['eventpass-discount', 'same-tier', 'billing'],
      variables: {
        email: () => `e2e-epdsamet-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Same Tier User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional, webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'get-price',
          name: 'Get EventPass Price (Professional)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Professional',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Full price or no discount for same-tier purchase.' },
    },
    {
      id: 'epdiscount-catalog-price',
      name: 'Price From PricingCatalog',
      description: 'Modified base price in catalog — discount applied to updated base, not hardcoded default.',
      tags: ['eventpass-discount', 'pricing-catalog', 'billing'],
      variables: {
        email: () => `e2e-epdcat-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Catalog Price User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'update-catalog',
          name: 'Update EventPass Price in Catalog',
          method: 'PUT',
          path: '/platform/pricing',
          auth: 'jwt',
          body: { eventPasses: { professional: { basePriceCents: 15000 } } },
          expect: { status: [200] },
        },
        {
          id: 'get-price',
          name: 'Get Discounted Price (uses catalog base)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Professional',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Discount applied to $150 catalog price, not hardcoded $199 default.' },
    },
    {
      id: 'epdiscount-catalog-discount',
      name: 'Discount % From PricingCatalog',
      description: 'Modified discount percentage in catalog. Updated discount % applied to price calculation.',
      tags: ['eventpass-discount', 'pricing-catalog', 'billing'],
      variables: {
        email: () => `e2e-epdcatd-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EP Catalog Discount User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register SuperAdmin User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'update-catalog-discount',
          name: 'Update Tier-Jump Discount % in Catalog',
          method: 'PUT',
          path: '/platform/pricing',
          auth: 'jwt',
          body: { eventPassDiscounts: { oneJump: 50 } },
          expect: { status: [200] },
        },
        {
          id: 'get-price',
          name: 'Get Discounted Price (uses updated % from catalog)',
          method: 'GET',
          path: '/billing/event-pass/price?tier=Professional',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: '50% discount applied instead of default 40% for 1-jump.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category AB: Stripe Customer Portal & Subscription Management (6 scenarios)
// ---------------------------------------------------------------------------
const CUSTOMER_PORTAL_CATEGORY = {
  id: 'customer-portal',
  name: 'Stripe Customer Portal',
  icon: 'ExternalLink',
  description: 'Stripe Customer Portal for subscription management. Subscription changes handled via webhooks.',
  scenarios: [
    {
      id: 'portal-create-session',
      name: 'Create Portal Session',
      description: 'POST /api/billing/customer-portal — returns Stripe portal URL for subscription management.',
      tags: ['customer-portal', 'stripe', 'subscription', 'billing'],
      variables: {
        email: () => `e2e-portal-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Portal Create User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, with active subscription)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'create-portal',
          name: 'Create Customer Portal Session',
          method: 'POST',
          path: '/billing/customer-portal',
          auth: 'jwt',
          extractors: { portalUrl: 'url' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Portal URL returned. Opens Stripe hosted portal for plan management.' },
    },
    {
      id: 'portal-upgrade-webhook',
      name: 'Subscription Upgraded via Portal',
      description: 'customer.subscription.updated webhook received after portal upgrade. Plan tier updated, limits increased.',
      tags: ['customer-portal', 'stripe', 'webhook', 'upgrade', 'billing'],
      variables: {
        email: () => `e2e-portalup-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Portal Upgrade User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'After Stripe webhook: plan tier updated to Professional. TTS + AI unlocked.' },
    },
    {
      id: 'portal-downgrade-webhook',
      name: 'Subscription Downgraded via Portal',
      description: 'customer.subscription.updated webhook received after portal downgrade. Plan tier reduced, features removed.',
      tags: ['customer-portal', 'stripe', 'webhook', 'downgrade', 'billing'],
      variables: {
        email: () => `e2e-portaldown-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Portal Downgrade User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'After Stripe webhook: plan tier reduced to Starter. TTS + AI removed.' },
    },
    {
      id: 'portal-cancel-webhook',
      name: 'Subscription Cancelled via Portal',
      description: 'customer.subscription.deleted webhook received. Graceful degradation to free tier.',
      tags: ['customer-portal', 'stripe', 'webhook', 'cancel', 'billing'],
      variables: {
        email: () => `e2e-portalcancel-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Portal Cancel User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'After cancellation webhook: degraded to free tier. Included hours gone.' },
    },
    {
      id: 'portal-no-subscription',
      name: 'Portal for Non-Subscriber',
      description: 'POST /api/billing/customer-portal for user with no active subscription.',
      tags: ['customer-portal', 'stripe', 'no-subscription', 'billing'],
      variables: {
        email: () => `e2e-portalnone-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Portal No Sub User',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (free tier)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'create-portal',
          name: 'Create Portal (no subscription)',
          method: 'POST',
          path: '/billing/customer-portal',
          auth: 'jwt',
          expect: { status: [404, 400] },
        },
      ],
      expectedState: { description: '404 or appropriate error for non-subscriber.' },
    },
    {
      id: 'portal-auth-required',
      name: 'Portal Requires JWT',
      description: 'POST /api/billing/customer-portal without JWT. Returns 401 Unauthorized.',
      tags: ['customer-portal', 'auth', 'security'],
      variables: {},
      steps: [
        {
          id: 'create-portal-no-auth',
          name: 'Create Portal (no auth)',
          method: 'POST',
          path: '/billing/customer-portal',
          auth: 'none',
          expect: { status: [401] },
        },
      ],
      expectedState: { description: '401 Unauthorized returned. JWT required.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category O: Subscription Lifecycle Transitions (12 scenarios)
// ---------------------------------------------------------------------------
const SUBSCRIPTION_LIFECYCLE_CATEGORY = {
  id: 'subscription-lifecycle',
  name: 'Subscription Transitions',
  icon: 'RefreshCw',
  description: 'Upgrade, downgrade, cancel, renewal, and Zoom Marketplace webhook-driven plan changes.',
  scenarios: [
    {
      id: 'sub-upgrade-free-to-starter',
      name: 'Upgrade Free → Starter',
      description: 'Register a free user, then seed a Starter subscription. Verifies included hours appear and free tier is replaced.',
      tags: ['subscription', 'upgrade', 'starter', 'lifecycle'],
      variables: {
        email: () => `e2e-upgfreestart-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Upgrade Free to Starter',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Free User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-starter-sub',
          name: 'Seed Starter Subscription',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant Tier (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify Entitlement (subscription points)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Free tier replaced by 60 min/mo included hours. TTS not available on Starter.' },
    },
    {
      id: 'sub-upgrade-starter-to-pro',
      name: 'Upgrade Starter → Professional',
      description: 'Seed Starter user then upgrade to Professional. Verifies TTS becomes available, included hours increase to 300.',
      tags: ['subscription', 'upgrade', 'professional', 'lifecycle'],
      variables: {
        email: () => `e2e-upgstartpro-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Upgrade Starter to Pro',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-pro-sub',
          name: 'Seed Professional Subscription',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 300, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-pro',
          name: 'Seed Tenant Tier (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: '300 min/mo included. TTS unlocked. Max 10 users, 3 languages.' },
    },
    {
      id: 'sub-upgrade-pro-to-biz',
      name: 'Upgrade Professional → Business',
      description: 'Seed Professional user then upgrade to Business. Verifies 6000 min/mo, 100 users, 30 languages.',
      tags: ['subscription', 'upgrade', 'business', 'lifecycle'],
      variables: {
        email: () => `e2e-upgprobiz-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Upgrade Pro to Business',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-biz-sub',
          name: 'Seed Business Subscription',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 6000, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-biz',
          name: 'Seed Tenant Tier (Business)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: '6000 min/mo included. Max 100 users, 30 languages, all features.' },
    },
    {
      id: 'sub-downgrade-pro-to-starter',
      name: 'Downgrade Pro → Starter',
      description: 'Seed Professional user then downgrade to Starter. Verifies TTS + AI removed, language limit = 1, included hours = 60.',
      tags: ['subscription', 'downgrade', 'starter', 'lifecycle'],
      variables: {
        email: () => `e2e-dwnprost-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Downgrade Pro to Starter',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-starter-sub',
          name: 'Seed Starter Subscription (post-downgrade state)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant Tier (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'TTS + AI removed. Max 1 language, 3 users. 60 min/mo included.' },
    },
    {
      id: 'sub-downgrade-starter-to-free',
      name: 'Cancel Subscription → Free',
      description: 'Seed Starter user then cancel subscription. Verifies included hours gone, free tier active, purchased points preserved.',
      tags: ['subscription', 'cancel', 'free', 'lifecycle'],
      variables: {
        email: () => `e2e-cancsub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Cancel to Free',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify Entitlement (no subscription points)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'No included hours. Free tier active (10 min/day). Purchased points, if any, preserved.' },
    },
    {
      id: 'sub-cancel-end-of-period',
      name: 'Cancel at Period End',
      description: 'Create subscription with future end date. User keeps full access until billing period ends, then falls to free tier.',
      tags: ['subscription', 'cancel', 'period-end', 'lifecycle'],
      variables: {
        email: () => `e2e-cancperiod-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Cancel at Period End',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-active',
          name: 'Seed Active Subscription (period ends in 15 days)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant Tier (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Full Starter access until period end. cancelling_at_period_end flag shown.' },
    },
    {
      id: 'sub-cancel-immediate',
      name: 'Cancel with Immediate Effect',
      description: 'Cancel subscription mid-cycle. Service stops immediately, graceful degradation to free tier.',
      tags: ['subscription', 'cancel', 'immediate', 'lifecycle'],
      variables: {
        email: () => `e2e-cancimm-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Cancel Immediate',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        entitlementStep(),
      ],
      expectedState: { description: 'Subscription terminated. Only free tier (10 min/day) available.' },
    },
    {
      id: 'sub-renewal-success',
      name: 'Subscription Renewal',
      description: 'Simulate a renewal by re-seeding subscription with reset included hours and advanced billing period.',
      tags: ['subscription', 'renewal', 'billing', 'lifecycle'],
      variables: {
        email: () => `e2e-renewal-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Renewal Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-exhausted',
          name: 'Seed Subscription (60/60 used — pre-renewal)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 60 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-sub-renewed',
          name: 'Seed Subscription (0/60 used — post-renewal)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant Tier (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Included hours reset to 60/60. Billing period advanced. New cycle begins.' },
    },
    {
      id: 'sub-reactivate-after-cancel',
      name: 'Reactivate Cancelled Sub',
      description: 'Register user, cancel subscription, then re-subscribe. Verifies clean state and new billing period.',
      tags: ['subscription', 'reactivate', 'lifecycle'],
      variables: {
        email: () => `e2e-reactsub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Reactivate Sub',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-active',
          name: 'Seed Starter Subscription (reactivated)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Clean reactivated state. Fresh 60 min/mo included. No residual from prior period.' },
    },
    {
      id: 'sub-zoom-marketplace-add',
      name: 'Zoom Marketplace Sub Added',
      description: 'Simulate Zoom Marketplace subscription_added webhook by seeding the resulting plan state. Verifies IZoomPlanMappingService mapping.',
      tags: ['subscription', 'zoom', 'webhook', 'lifecycle'],
      variables: {
        email: () => `e2e-zoomaddsub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Zoom Marketplace Add',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-zoom',
          name: 'Seed Zoom Marketplace Subscription (Starter)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-zoom-starter',
          name: 'Seed Tenant (Zoom Starter plan_zoom_meeting_starter_v2)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Zoom Marketplace plan correctly mapped via IZoomPlanMappingService. Starter limits active.' },
    },
    {
      id: 'sub-zoom-marketplace-update',
      name: 'Zoom Marketplace Plan Change',
      description: 'Simulate Zoom Marketplace subscription_updated webhook — plan tier updated, limits changed.',
      tags: ['subscription', 'zoom', 'webhook', 'upgrade', 'lifecycle'],
      variables: {
        email: () => `e2e-zoomupdatesub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Zoom Marketplace Update',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-pro',
          name: 'Seed Updated Subscription (Professional — post-update state)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 300, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant-pro',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Plan tier updated to Professional. 300 min/mo, TTS + AI unlocked.' },
    },
    {
      id: 'sub-zoom-marketplace-delete',
      name: 'Zoom Marketplace Cancellation',
      description: 'Simulate Zoom Marketplace subscription_deleted webhook — graceful degradation to free tier.',
      tags: ['subscription', 'zoom', 'webhook', 'cancel', 'lifecycle'],
      variables: {
        email: () => `e2e-zoomcancelsub-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Zoom Marketplace Delete',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        entitlementStep(),
      ],
      expectedState: { description: 'Subscription removed. Graceful degradation to free tier. 10 min/day available.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category P: Payment Failures & Recovery (6 scenarios)
// ---------------------------------------------------------------------------
const PAYMENT_FAILURES_CATEGORY = {
  id: 'payment-failures',
  name: 'Payment Failures & Recovery',
  icon: 'AlertTriangle',
  description: 'Stripe payment failures, dunning, refunds, and idempotent webhook processing.',
  scenarios: [
    {
      id: 'pay-hourpack-purchase-fail',
      name: 'Hour Pack Purchase Fails',
      description: 'Payment intent fails during hour pack purchase. No hours added, purchased balance unchanged. User notified via UI error.',
      tags: ['payment', 'failure', 'hour-pack', 'stripe'],
      variables: {
        email: () => `e2e-hourpackfail-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'HourPack Fail',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Subscriber',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-starter-sub',
          name: 'Seed Starter Subscription (no purchased hours)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'No purchased hours added. 60 min included hours intact. Error shown in UI.' },
    },
    {
      id: 'pay-eventpass-purchase-fail',
      name: 'EventPass Purchase Fails',
      description: 'Checkout session abandoned before payment. No event pass created, no charge.',
      tags: ['payment', 'failure', 'event-pass', 'stripe'],
      variables: {
        email: () => `e2e-epassfail-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'EventPass Fail',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter, product=webinar)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'webinar' },
          expect: { status: [200] },
        },
        {
          id: 'verify-no-pass',
          name: 'Verify No EventPass Exists',
          method: 'GET',
          path: '/billing/passes/me',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'No EventPass created. No charge. User can retry purchase.' },
    },
    {
      id: 'pay-sub-renewal-fail-grace',
      name: 'Subscription Renewal Fails (Grace Period)',
      description: 'Subscription renewal payment fails. Service continues during grace period. invoice.payment_failed webhook received.',
      tags: ['payment', 'failure', 'subscription', 'grace', 'stripe'],
      variables: {
        email: () => `e2e-renewalfailgrace-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Renewal Fail Grace',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-sub-active',
          name: 'Seed Active Subscription (in grace period)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'zoom', status: 'active', includedHours: { totalMinutes: 60, usedMinutes: 0 } },
          expect: { status: [200] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Service continues during grace period. Payment failure banner shown. User prompted to update payment method.' },
    },
    {
      id: 'pay-sub-renewal-fail-blocked',
      name: 'Subscription Blocked After Dunning',
      description: 'Multiple payment failures exhaust grace period. User blocked from creating sessions.',
      tags: ['payment', 'failure', 'subscription', 'blocked', 'dunning'],
      variables: {
        email: () => `e2e-dunningblocked-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Dunning Blocked',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-subscription-expired',
          name: 'Seed Subscription (status=expired after dunning)',
          method: 'POST',
          path: '/test/seed/subscription',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', channel: 'direct', status: 'expired' },
          expect: { status: [200] },
        },
        entitlementStep(),
      ],
      expectedState: { usageCredits: 0, description: 'Subscription expired after dunning, so no subscription points are granted. Session creation is a points shortfall and is blocked; "Payment Required" blocking state.' },
    },
    {
      id: 'pay-card-expired',
      name: 'Card Expired',
      description: 'payment_method.detached webhook received. User prompted to update payment method. Subscription in past_due state.',
      tags: ['payment', 'failure', 'card', 'expired', 'stripe'],
      variables: {
        email: () => `e2e-cardexp-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Card Expired',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Payment method detached. Subscription past_due. "Update payment method" prompt shown via Stripe Customer Portal.' },
    },
    {
      id: 'pay-double-charge-prevention',
      name: 'Idempotent Webhook Processing',
      description: 'Duplicate Stripe webhook with same event ID is a no-op. Second delivery does not double-charge or create duplicate records.',
      tags: ['payment', 'idempotency', 'webhook', 'stripe'],
      variables: {
        email: () => `e2e-idempotent-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Idempotent Webhook',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        entitlementStep(),
      ],
      expectedState: { description: 'Stripe event ID deduplication enforced. Second webhook with same stripe_event_id is rejected. Points balance unchanged.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category R: Account Lifecycle & Compliance (8 scenarios)
// ---------------------------------------------------------------------------
const ACCOUNT_LIFECYCLE_CATEGORY = {
  id: 'account-lifecycle',
  name: 'Account Lifecycle & Compliance',
  icon: 'UserX',
  description: 'Account deletion cascades, suspension, OAuth registration flows, and provider linking.',
  scenarios: [
    {
      id: 'account-delete-cascade',
      name: 'Admin Deletes User',
      description: 'Register user, then delete via admin endpoint. Verifies cascade: EmailLookup, ProviderLookup, LinkedIdentity all removed by UserCascadingDeleteService.',
      tags: ['account', 'delete', 'cascade', 'compliance'],
      variables: {
        email: () => `e2e-delcascade-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Delete Cascade Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', userId: 'userId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'delete-tenant',
          name: 'Delete Tenant (cascade all user documents)',
          method: 'DELETE',
          path: '/test/seed/tenant/{tenantId}',
          auth: 'jwt',
          expect: { status: [200, 204] },
        },
        {
          id: 'verify-deleted',
          name: 'Verify Tenant Gone (404)',
          method: 'GET',
          path: '/customer/{tenantId}',
          auth: 'function-key',
          expect: { status: [404] },
        },
      ],
      expectedState: { description: 'Tenant and all associated documents removed. EmailLookup, ProviderLookup, LinkedIdentity cascaded by UserCascadingDeleteService.' },
    },
    {
      id: 'account-delete-tenant',
      name: 'Admin Deletes Entire Tenant',
      description: 'Delete an entire tenant. Verifies all users, sessions, and billing records are purged.',
      tags: ['account', 'delete', 'tenant', 'compliance'],
      variables: {
        email: () => `e2e-deltenant-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Delete Tenant Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'delete-tenant',
          name: 'Delete Entire Tenant',
          method: 'DELETE',
          path: '/test/seed/tenant/{tenantId}',
          auth: 'jwt',
          expect: { status: [200, 204] },
        },
      ],
      expectedState: { description: 'All tenant data purged. Points ledger, subscription, and user documents removed.' },
    },
    {
      id: 'account-suspend',
      name: 'Suspend User',
      description: 'Set user status=suspended via admin. Verifies LifecycleStateMiddleware blocks session creation.',
      tags: ['account', 'suspend', 'lifecycle'],
      variables: {
        email: () => `e2e-suspend-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Suspend Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Account suspended. LifecycleStateMiddleware blocks session creation. "Account Suspended" message shown.' },
    },
    {
      id: 'account-oauth-google',
      name: 'Google OAuth Registration',
      description: 'Register via Google provider. Verifies correct plan assigned, JWT issued, provider linked.',
      tags: ['account', 'oauth', 'google', 'registration'],
      variables: {
        email: () => `e2e-googleoauth-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Google OAuth Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (simulates Google OAuth result)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify Free Tier Plan (default for new Google OAuth users)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Google OAuth user created. Default free tier plan assigned. JWT issued. Provider linked in ProviderLookup.' },
    },
    {
      id: 'account-oauth-zoom',
      name: 'Zoom OAuth Registration',
      description: 'Register via Zoom provider. Verifies Zoom token stored (tokenStorage.StoreTokensAsync), plan mapped via IZoomPlanMappingService.',
      tags: ['account', 'oauth', 'zoom', 'registration'],
      variables: {
        email: () => `e2e-zoomoauth-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Zoom OAuth Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (simulates Zoom OAuth result)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify Plan (default free tier for new Zoom OAuth users)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Zoom OAuth user created. Zoom tokens stored in Redis (tokenStorage.StoreTokensAsync). Plan mapped.' },
    },
    {
      id: 'account-oauth-microsoft',
      name: 'Microsoft OAuth Registration',
      description: 'Register via Microsoft provider. Verifies correct plan assigned, JWT issued.',
      tags: ['account', 'oauth', 'microsoft', 'registration'],
      variables: {
        email: () => `e2e-msoauth-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Microsoft OAuth Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (simulates Microsoft OAuth result)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify Plan (default free tier)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Microsoft OAuth user created. Correct plan assigned. JWT issued. Provider linked.' },
    },
    {
      id: 'account-link-multiple',
      name: 'Link Multiple OAuth Providers',
      description: 'Register user, then link Google + Microsoft providers. Verifies both appear in GetLinkedProvidersAsync.',
      tags: ['account', 'oauth', 'link', 'providers'],
      variables: {
        email: () => `e2e-multiprovider-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Multi-Provider Link',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Base User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', userId: 'user.userId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'get-linked-providers',
          name: 'Get Linked Providers',
          method: 'GET',
          path: '/auth/providers?userId={userId}',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'User has email/password as base auth. Both Google and Microsoft providers linked. GetLinkedProvidersAsync returns both.' },
    },
    {
      id: 'account-unlink-last',
      name: 'Unlink Last Auth Method (Blocked)',
      description: 'Try to unlink the sole OAuth provider. Error: UnlinkResult.LastAuthMethod() returned — user would have no login method.',
      tags: ['account', 'oauth', 'unlink', 'safety'],
      variables: {
        email: () => `e2e-unlinksole-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Unlink Last Provider',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User (email/password only)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', userId: 'user.userId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'get-linked-providers',
          name: 'Get Linked Providers (verify only email)',
          method: 'GET',
          path: '/auth/providers?userId={userId}',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Attempting to unlink sole auth method returns LastAuthMethod error. User account stays accessible.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category S: Password & Auth Edge Cases (5 scenarios)
// ---------------------------------------------------------------------------
const PASSWORD_AUTH_CATEGORY = {
  id: 'password-auth',
  name: 'Password & Auth Edge Cases',
  icon: 'KeyRound',
  description: 'Password reset flows, rate limiting, JWT refresh, and expired token handling.',
  scenarios: [
    {
      id: 'auth-password-reset',
      name: 'Forgot Password → Reset',
      description: 'Full password reset flow: POST /auth/forgot-password → token email → POST /auth/reset-password. New password accepted.',
      tags: ['auth', 'password', 'reset', 'flow'],
      variables: {
        email: () => `e2e-pwreset-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        newPassword: 'NewTestPass456!',
        name: 'Password Reset Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'forgot-password',
          name: 'Request Password Reset',
          method: 'POST',
          path: '/auth/forgot-password',
          auth: 'none',
          body: { email: '{email}' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Password reset email sent. Token is 1-hour TTL, one-time use. After /auth/reset-password, new password accepted.' },
    },
    {
      id: 'auth-password-reset-expired',
      name: 'Expired Reset Token',
      description: 'Use a password reset token after 1 hour. Should return 400 with a clean "token expired" message.',
      tags: ['auth', 'password', 'reset', 'expired'],
      variables: {
        email: () => `e2e-pwexpired-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Expired Token Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'reset-with-expired-token',
          name: 'Reset with Expired Token',
          method: 'POST',
          path: '/auth/reset-password',
          auth: 'none',
          body: { token: 'expired-token-placeholder', newPassword: 'NewPass123!' },
          expect: { status: [400] },
        },
      ],
      expectedState: { description: '400 error with clean "token expired or invalid" message. No password change occurs.' },
    },
    {
      id: 'auth-password-reset-rate-limit',
      name: 'Reset Rate Limit (3/hour)',
      description: 'Request 4 password resets for same email. 4th request returns 429 Too Many Requests. PasswordResetService rate limit = 3/hour.',
      tags: ['auth', 'password', 'reset', 'rate-limit'],
      variables: {
        email: () => `e2e-pwratelimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Rate Limit Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'reset-attempt-1',
          name: 'Reset Request 1 (OK)',
          method: 'POST',
          path: '/auth/forgot-password',
          auth: 'none',
          body: { email: '{email}' },
          expect: { status: [200] },
        },
        {
          id: 'reset-attempt-2',
          name: 'Reset Request 2 (OK)',
          method: 'POST',
          path: '/auth/forgot-password',
          auth: 'none',
          body: { email: '{email}' },
          expect: { status: [200] },
        },
        {
          id: 'reset-attempt-3',
          name: 'Reset Request 3 (OK)',
          method: 'POST',
          path: '/auth/forgot-password',
          auth: 'none',
          body: { email: '{email}' },
          expect: { status: [200] },
        },
        {
          id: 'reset-attempt-4',
          name: 'Reset Request 4 (Rate Limited — 429)',
          method: 'POST',
          path: '/auth/forgot-password',
          auth: 'none',
          body: { email: '{email}' },
          expect: { status: [429] },
        },
      ],
      expectedState: { description: '4th reset request blocked with 429. PasswordResetService enforces 3 tokens/hour per email.' },
    },
    {
      id: 'auth-jwt-expired',
      name: 'Expired JWT Token',
      description: 'Call API with an expired JWT. Returns 401, client redirects to login.',
      tags: ['auth', 'jwt', 'expired', 'security'],
      variables: {
        email: () => `e2e-jwtexpired-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'JWT Expired Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'call-with-expired-jwt',
          name: 'Call API with Expired JWT',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Fresh JWT works (200). Expired JWT returns 401. fetchWithAuth auto-redirects to login on 401.' },
    },
    {
      id: 'auth-jwt-refresh',
      name: 'JWT Refresh Flow',
      description: 'Near-expiry token triggers auto-refresh via fetchWithAuth. New token issued seamlessly.',
      tags: ['auth', 'jwt', 'refresh', 'session'],
      variables: {
        email: () => `e2e-jwtrefresh-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'JWT Refresh Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'login',
          name: 'Login (get fresh JWT)',
          method: 'POST',
          path: '/auth/login/email',
          auth: 'none',
          body: { email: '{email}', password: '{password}' },
          extractors: { jwt: 'token' },
          expect: { status: [200] },
        },
        {
          id: 'verify-entitlement',
          name: 'Verify API Call Works with JWT',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'JWT refresh flow works via fetchWithAuth. Near-expiry JWT is refreshed before the call. Seamless to user.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category T: Multi-Tenant Isolation (4 scenarios)
// ---------------------------------------------------------------------------
const MULTI_TENANT_CATEGORY = {
  id: 'multi-tenant',
  name: 'Multi-Tenant Isolation',
  icon: 'Building2',
  description: 'Cross-tenant access, billing isolation, concurrent limits, and admin scope enforcement.',
  scenarios: [
    {
      id: 'tenant-cross-billing',
      name: 'Cross-Tenant Billing Isolation',
      description: 'Create two tenants, verify each reads only its own points balance.',
      tags: ['isolation', 'multi-tenant', 'billing'],
      variables: {
        emailA: () => `e2e-billisolA-${Date.now()}@test.meetingsync.ai`,
        emailB: () => `e2e-billisolB-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Billing Isolation',
      },
      steps: [
        {
          id: 'register-a',
          name: 'Register Tenant A',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{emailA}', password: '{password}', name: 'Tenant A Billing' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'register-b',
          name: 'Register Tenant B',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{emailB}', password: '{password}', name: 'Tenant B Billing' },
          extractors: { tenantIdB: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'verify-entitlement-a',
          name: 'Verify Tenant A Reads Only Its Own Points',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Tenant A entitlement reports Tenant A usageCredits only; Tenant B grants never appear. Completely independent points ledgers in Cosmos.' },
    },
    {
      id: 'tenant-concurrent-limit-isolation',
      name: 'Concurrent Limits Per Tenant',
      description: 'Two tenants at their plan limits — Tenant A\'s limit doesn\'t affect Tenant B.',
      tags: ['isolation', 'multi-tenant', 'concurrent', 'limits'],
      variables: {
        emailA: () => `e2e-concurA-${Date.now()}@test.meetingsync.ai`,
        emailB: () => `e2e-concurB-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Concurrent Isolation',
      },
      steps: [
        {
          id: 'register-a',
          name: 'Register Tenant A (Starter = 1 concurrent)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{emailA}', password: '{password}', name: 'Tenant A Concurrent' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-a',
          name: 'Seed Tenant A (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-b',
          name: 'Register Tenant B (Starter = 1 concurrent)',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{emailB}', password: '{password}', name: 'Tenant B Concurrent' },
          extractors: { tenantIdB: 'tenantId' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-b',
          name: 'Seed Tenant B (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantIdB}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Each tenant has its own Redis concurrent session counter. Tenant A at limit has no effect on Tenant B\'s limit.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category U: Webhook Reliability (4 scenarios)
// ---------------------------------------------------------------------------
const WEBHOOK_RELIABILITY_CATEGORY = {
  id: 'webhook-reliability',
  name: 'Webhook Reliability',
  icon: 'Zap',
  description: 'Stripe and Zoom Marketplace webhook signature validation and idempotent processing.',
  scenarios: [
    {
      id: 'webhook-stripe-signature-valid',
      name: 'Valid Stripe Webhook',
      description: 'Verify a valid Stripe webhook signature is accepted (HTTP 200). Event processed, state changes applied.',
      tags: ['webhook', 'stripe', 'signature', 'valid'],
      variables: {
        email: () => `e2e-stripevalid-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Stripe Valid Webhook',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'verify-state',
          name: 'Verify Tenant State (webhook would update this)',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Valid Stripe webhook with correct HMAC-SHA256 signature returns 200. Event processed exactly once.' },
    },
    {
      id: 'webhook-stripe-signature-invalid',
      name: 'Invalid Stripe Webhook',
      description: 'Stripe webhook with wrong signature is rejected with 401. No state change occurs.',
      tags: ['webhook', 'stripe', 'signature', 'invalid', 'security'],
      variables: {
        email: () => `e2e-stripeinvalid-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Stripe Invalid Webhook',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'call-webhook-bad-sig',
          name: 'POST to Stripe webhook endpoint (no valid sig)',
          method: 'POST',
          path: '/stripe/webhook',
          auth: 'none',
          body: { type: 'invoice.payment_failed', data: { object: { customer: 'cus_test' } } },
          expect: { status: [400, 401] },
        },
      ],
      expectedState: { description: 'Invalid signature rejected with 400/401. No payment state change. No duplicate processing.' },
    },
    {
      id: 'webhook-zoom-marketplace',
      name: 'Zoom Marketplace Webhook',
      description: 'Simulate marketplace.subscription_added event. Plan correctly assigned via IZoomPlanMappingService.',
      tags: ['webhook', 'zoom', 'marketplace', 'plan'],
      variables: {
        email: () => `e2e-zoomwebhook-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Zoom Webhook Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-zoom',
          name: 'Seed Tenant (Starter via Zoom plan ID)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'verify-plan-mapping',
          name: 'Verify Plan Mapping Applied',
          method: 'GET',
          path: '/billing/ios/entitlement',
          auth: 'jwt',
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Zoom Marketplace subscription_added webhook processed. Plan mapped via IZoomPlanMappingService. Correct tier limits active.' },
    },
    {
      id: 'webhook-meeting-delivery',
      name: 'Meeting Summary Webhook',
      description: 'Post-session meeting summary delivery to customer webhook URL. Customer receives payload with HMAC signature.',
      tags: ['webhook', 'meeting', 'delivery', 'summary'],
      variables: {
        email: () => `e2e-meetingwebhook-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Meeting Delivery Webhook',
      },
      steps: [
        {
          id: 'register',
          name: 'Register User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Professional)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Professional', productType: 'meeting' },
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Customer webhook configured. Post-session delivery sends summary with HMAC-SHA256 signature via MeetingWebhookDeliveryService.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category V: Concurrent Session Enforcement (4 scenarios)
// ---------------------------------------------------------------------------
const CONCURRENT_SESSION_CATEGORY = {
  id: 'concurrent-sessions',
  name: 'Concurrent Session Enforcement',
  icon: 'Layers',
  description: 'Redis atomic counters enforce plan concurrent session limits. Tests at-limit, over-limit, slot release, and unlimited plans.',
  scenarios: [
    {
      id: 'concurrent-at-limit',
      name: 'At Max Concurrent Sessions',
      description: 'Create sessions up to plan limit (Starter=1, Pro=5). All succeed. Next session is blocked.',
      tags: ['concurrent', 'sessions', 'limit', 'plan'],
      variables: {
        email: () => `e2e-concuratlimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'At Concurrent Limit',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Starter User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant (Starter — 1 concurrent session)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-session',
          name: 'Register Host Session (uses 1/1 slot)',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          body: (() => { const s = `session-${Date.now()}`; return { tenantId: '{tenantId}', sessionId: s, hostId: `host-${s}`, metadata: { source: 'integration-test' }, testRunId: s }; })(),
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Starter plan at 1/1 concurrent sessions. Next session create will return 403. ConcurrentSessionLimitService enforcing via Redis Lua TOCTOU-safe counter.' },
    },
    {
      id: 'concurrent-over-limit',
      name: 'Over Max Concurrent Sessions',
      description: 'Create one more session than plan limit. 403 returned with clear "max sessions reached" message.',
      tags: ['concurrent', 'sessions', 'over-limit', 'blocked'],
      variables: {
        email: () => `e2e-concuroverlimit-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Over Concurrent Limit',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Starter User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-starter',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-session-1',
          name: 'Register Session 1 (OK — uses 1/1 slot)',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          body: (() => { const s = `session1-${Date.now()}`; return { tenantId: '{tenantId}', sessionId: s, hostId: `host-${s}`, metadata: { source: 'integration-test' }, testRunId: s }; })(),
          expect: { status: [200] },
        },
      ],
      expectedState: { description: '1/1 slots used. Attempting to create Session 2 returns 403 MAX_CONCURRENT_SESSIONS_REACHED. UI shows clear error message.' },
    },
    {
      id: 'concurrent-release-slot',
      name: 'End Session → Free Slot',
      description: 'End one session, then create a new session. Verifies ReleaseSessionSlotAsync works correctly.',
      tags: ['concurrent', 'sessions', 'release', 'slot'],
      variables: {
        email: () => `e2e-concurrelease-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Release Slot Test',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Starter User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant',
          name: 'Seed Tenant (Starter)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Starter', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-session',
          name: 'Register Host Session (1/1 slot used)',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          body: (() => { const s = `session-${Date.now()}`; return { tenantId: '{tenantId}', sessionId: s, hostId: `host-${s}`, metadata: { source: 'integration-test' }, testRunId: s }; })(),
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'After ending session, ReleaseSessionSlotAsync decrements counter. New session can be created (0/1 → slot available).' },
    },
    {
      id: 'concurrent-unlimited',
      name: 'Unlimited Plan (-1)',
      description: 'Business plan (maxConcurrentSessions=20) or Unlimited plan (-1). No concurrent limit enforced.',
      tags: ['concurrent', 'sessions', 'unlimited', 'business'],
      variables: {
        email: () => `e2e-concurunlim-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'Unlimited Concurrent',
      },
      steps: [
        {
          id: 'register',
          name: 'Register Business User',
          method: 'POST',
          path: '/auth/register',
          auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'tenantId', jwt: 'token' },
          expect: { status: [200, 201] },
        },
        {
          id: 'seed-tenant-biz',
          name: 'Seed Tenant (Business — 20 concurrent sessions)',
          method: 'POST',
          path: '/test/seed/tenant',
          auth: 'function-key',
          body: { tenantId: '{tenantId}', tier: 'Business', productType: 'meeting' },
          expect: { status: [200] },
        },
        {
          id: 'register-session-1',
          name: 'Register Session 1 (OK)',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          body: (() => { const s = `session1-${Date.now()}`; return { tenantId: '{tenantId}', sessionId: s, hostId: `host-${s}`, metadata: { source: 'integration-test' }, testRunId: s }; })(),
          expect: { status: [200] },
        },
        {
          id: 'register-session-2',
          name: 'Register Session 2 (OK — Business allows multiple)',
          method: 'POST',
          path: '/test/session/register-host',
          auth: 'function-key',
          body: (() => { const s = `session2-${Date.now()}`; return { tenantId: '{tenantId}', sessionId: s, hostId: `host-${s}`, metadata: { source: 'integration-test' }, testRunId: s }; })(),
          expect: { status: [200] },
        },
      ],
      expectedState: { description: 'Business plan supports 20 concurrent sessions. Multiple sessions succeed. ConcurrentSessionLimitService does not block.' },
    },
  ],
};

// ---------------------------------------------------------------------------
// Category: iOS Realtime Billing (PR #1083)
// ---------------------------------------------------------------------------
//
// Validates the backend half of PR #1083 — the gated realtime rail, iOS plan
// provisioning, and the bypass/negative paths — by driving the SAME Functions
// endpoints the iOS app calls. No device required: the realtime gate encodes
// its decision in the HTTP status of POST /realtime-session/start
//   200 = allowed · 402 = usage_balance_insufficient (points shortage, with
//   usageShortfallMilli and options [buy_pack]) · 409 = conflict (duplicate
//   clientReferenceId) · 426 = usage_upgrade_required (no accepted quote) ·
//   400 = unknown_service / malformed · 401 = unauthenticated (JwtMiddleware)
// so these scenarios validate the gate/provisioning/bypass logic with the
// existing status-only assertion engine.
//
// Every Meeting start is priced in points, so each billable start is QUOTED
// first, the way every shipped client does: read the catalog's schema and rate
// versions, POST the same body with quoteOnly (expectedDurationMs = the
// server's 1,800,000 ms hold window), then start with the accepted digest and
// expiry. A whole-token body value keeps its extracted type, so the schema
// version is sent as a JSON number.
//
// Endpoints exercised (all on origin/main):
//   POST /auth/register                  (auth none)         — mint a tenant + JWT
//   POST /test/seed/customer-plan        (function-key)      — { tenantId, targetPlanId, reason }
//   POST /test/seed/subscription         (jwt)               — { tenantId, planId } (TTL-scoped test subscription)
//   POST /test/seed/ios-plan-backfill    (function-key)      — { dryRun, sampleLimit }
//   GET  /billing/catalog                (jwt)               — { billingSchemaVersion, usageCredits.rateVersion }
//   GET  /billing/ios/entitlement        (jwt)               — projection { tier, usageCredits, services }
//   POST /realtime-session/{start,heartbeat,end} (jwt)       — { sessionId, userToken, scenario, targetLang, sourceLang, quote fields }
//
// Scenario id convention: every id is prefixed `ios-`; first tag is always
// `ios`; second tag is the sub-domain (`ios-provisioning` / `ios-rail` /
// `ios-bypass` / `ios-backfill`) so the sidebar search box narrows within iOS.
//
// DEFERRED (require the response-field `assert` engine extension + new test
// verify/inject endpoints — see plan §Part C.3/C.4 in
// src/Functions/plans/2026-06-08-pr1083-user-case-test-plan.md):
//   • Entitlement cap/remaining VALUE assertions (not just 200).
//   • RealtimeUsage persisted-row evidence (durationMinutes tolerance,
//     exactly-one-row after double-end) — needs GET /test/verify/realtime-usage.
//   • ASNV2 lifecycle state machine (12 transitions) — needs
//     POST /test/seed/apple-notification + GET /test/verify/subscription.
//   • Backfill count/sample assertions — needs the `assert` engine.
// These are intentionally NOT stubbed here to avoid false-green scenarios.
// ---------------------------------------------------------------------------
/** Reads the catalog's billing schema and rate versions every quote is priced against. */
const IOS_CATALOG_STEP = {
  id: 'catalog', name: 'Read usage catalog (quote versions)', method: 'GET', path: '/billing/catalog', auth: 'jwt',
  extractors: { billingSchemaVersion: 'billingSchemaVersion', rateVersion: 'usageCredits.rateVersion' },
  expect: { status: [200] },
};

/** The versioned start body a quote and its start must share. */
const iosQuotedBody = body => ({
  ...body,
  billingSchemaVersion: '{billingSchemaVersion}',
  expectedRateVersion: '{rateVersion}',
  expectedDurationMs: 1800000,
});

/** Quote-only step for a start body; extracts the accepted digest and expiry. */
const iosQuoteStep = (id, name, body) => ({
  id: `${id}-quote`, name: `${name} — quote`, method: 'POST', path: '/realtime-session/start', auth: 'jwt',
  body: { ...iosQuotedBody(body), quoteOnly: true },
  extractors: { quoteDigest: 'quoteDigest', quoteExpiresAtUtc: 'expiresAtUtc' },
  expect: { status: [200] },
});

/** The start itself, carrying the digest the preceding quote of the SAME body accepted. */
const iosStartStep = (id, name, body, status, extra = {}) => ({
  id, name, method: 'POST', path: '/realtime-session/start', auth: 'jwt',
  body: {
    ...iosQuotedBody(body),
    expectedQuoteDigest: '{quoteDigest}',
    expectedQuoteExpiresAtUtc: '{quoteExpiresAtUtc}',
  },
  ...extra,
  expect: { status },
});

/** Quote, then start: the two steps every billable start takes. */
const iosQuotedStart = (id, name, body, status, extra) => [
  iosQuoteStep(id, name, body),
  iosStartStep(id, name, body, status, extra),
];

const IOS_REALTIME_BILLING_CATEGORY = {
  id: 'ios-realtime-billing',
  name: 'iOS Realtime Billing',
  icon: 'Smartphone',
  description: 'PR #1083 — Apple plan provisioning, gated realtime rail (409 self-conflict / liveness), and bypass/negative paths. Drives the same Functions endpoints the iOS app calls; the gate decision is the HTTP status of /realtime-session/start.',
  scenarios: [
    // -- Provisioning ------------------------------------------------------
    {
      id: 'ios-provisioning-ios-free-allowed',
      name: 'iOS Free plan → live translation allowed',
      description: 'A tenant on plan_ios_free_v1 resolves an entitlement, quotes a 강의/Lecture (live) start and is allowed to start it: the registration subscription\'s monthly points fund it.',
      tags: ['ios', 'ios-provisioning', 'apple'],
      variables: {
        email: () => `e2e-ios-free-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Free User',
        userToken: () => `ut-${Date.now()}`,
        sessionId: () => `ios-prov-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', userId: 'user.userId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        {
          id: 'entitlement', name: 'Get iOS Entitlement', method: 'GET', path: '/billing/ios/entitlement', auth: 'jwt',
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-lecture', 'Start Lecture (live) → allowed', { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200],
          { extractors: { clientReferenceId: 'clientReferenceId' } }),
        {
          id: 'end', name: 'End session (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        plan: 'plan_ios_free_v1',
        entitlement: '200 with usageCredits present',
        gate: 'quote 200, then start 200 (allowed, points-funded)',
      },
    },
    {
      id: 'ios-provisioning-no-caps-points-funded',
      name: 'No PerServiceCaps → still points-funded (no plan refusal)',
      description: 'Contrast case: a tenant on plan_meeting_free_v1 (PerServiceCaps == null). Points are the funding authority for every Meeting start, so a plan document without per-service caps is NOT a refusal: the quoted start is admitted (200). Requires plan_meeting_free_v1 to be seeded in the target env.',
      tags: ['ios', 'ios-provisioning'],
      variables: {
        email: () => `e2e-ios-nocaps-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS No-Caps User',
        userToken: () => `ut-${Date.now()}`,
        sessionId: () => `ios-nocaps-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-meeting-free', name: 'Assign plan_meeting_free_v1 (null caps)', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_meeting_free_v1', reason: 'scenario-test-nocaps' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-lecture', 'Start Lecture → 200 (points-funded)', { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200]),
        {
          id: 'end', name: 'End session (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        plan: 'plan_meeting_free_v1 (PerServiceCaps null)',
        gate: 'quote 200, then start 200 — a missing per-service cap never refuses a points-funded start',
      },
    },
    {
      id: 'ios-provisioning-three-modes-allowed',
      name: 'All three modes allowed (lecture / conversation / transcribe)',
      description: 'On plan_ios_free_v1, the three iOS realtime scenarios — 강의 (lecture→live), 대화 (conversation→twoWay), 받아쓰기 (transcribe→transcription) — are each quoted and started successfully (200). Distinct sessionIds avoid the self-conflict 409.',
      tags: ['ios', 'ios-provisioning'],
      variables: {
        email: () => `e2e-ios-modes-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Modes User',
        userToken: () => `ut-${Date.now()}`,
        sidLecture: () => `ios-lec-${Date.now()}`,
        sidConv: () => `ios-conv-${Date.now()}`,
        sidTrans: () => `ios-trn-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-lecture', 'Start Lecture (live)', { sessionId: '{sidLecture}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200]),
        ...iosQuotedStart('start-conversation', 'Start Conversation (twoWay)',
          { sessionId: '{sidConv}', userToken: '{userToken}', scenario: 'conversation', targetLang: 'en', sourceLang: 'ko' }, [200]),
        ...iosQuotedStart('start-transcribe', 'Start Transcribe (transcription)',
          { sessionId: '{sidTrans}', userToken: '{userToken}', scenario: 'transcribe', targetLang: 'ko', sourceLang: 'ko' }, [200]),
        {
          id: 'end-lecture', name: 'End Lecture (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sidLecture}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
        {
          id: 'end-conversation', name: 'End Conversation (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sidConv}', userToken: '{userToken}', scenario: 'conversation', routeGeneration: 1 },
          expect: { status: [200] },
        },
        {
          id: 'end-transcribe', name: 'End Transcribe (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sidTrans}', userToken: '{userToken}', scenario: 'transcribe', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: { gate: 'all three quoted starts return 200; all three sessions ended (self-cleaning)' },
    },
    // -- Gated rail: 409 self-conflict + liveness --------------------------
    {
      id: 'ios-rail-409-self-conflict',
      name: 'Same sessionId twice → 409 (self-conflict fix)',
      description: 'The headline 409 bug. Two starts with the SAME { userToken, sessionId } (→ same clientReferenceId {tenantId}:{userToken}:{sessionId}) — the first opens (200), the second, carrying the same still-valid quote, is rejected 409 by the Redis SET-NX guard. sessionId is generated once per run so re-runs do not collide.',
      tags: ['ios', 'ios-rail', 'conflict'],
      variables: {
        email: () => `e2e-ios-409-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS 409 User',
        userToken: () => `ut-409-${Date.now()}`,
        sessionId: () => `ios-409-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-first', 'Start #1 → 200 (opens session)', { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200]),
        iosStartStep('start-duplicate', 'Start #2 (same id, same quote) → 409 conflict', { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [409]),
        {
          id: 'end', name: 'End session (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: { gate: 'first quoted start 200, duplicate start 409 conflict' },
    },
    {
      id: 'ios-rail-fresh-id-allowed',
      name: 'Fresh sessionId after a start → allowed (not a global lock)',
      description: 'Proves the 409 is keyed on the clientReferenceId, not a blanket per-tenant lock: after one session is open, a quoted start with a DIFFERENT sessionId succeeds (200). Both are cleaned up.',
      tags: ['ios', 'ios-rail'],
      variables: {
        email: () => `e2e-ios-fresh-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Fresh-Id User',
        userToken: () => `ut-fresh-${Date.now()}`,
        sidA: () => `ios-a-${Date.now()}`,
        sidB: () => `ios-b-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-a', 'Start session A → 200', { sessionId: '{sidA}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200]),
        ...iosQuotedStart('start-b', 'Start session B (fresh id) → 200', { sessionId: '{sidB}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [200]),
        {
          id: 'end-a', name: 'End A (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sidA}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
        {
          id: 'end-b', name: 'End B (cleanup)', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sidB}', userToken: '{userToken}', scenario: 'lecture', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: { gate: 'both distinct-id quoted starts return 200' },
    },
    {
      id: 'ios-rail-lifecycle-start-heartbeat-end',
      name: 'Session lifecycle: start → heartbeat → heartbeat → end',
      description: 'Exercises the full liveness lifecycle of the gated rail: open a quoted session, advance liveness twice (heartbeat 200), then finalize (end 200). A heartbeat for an unknown session would 404 — here it tracks the open session.',
      tags: ['ios', 'ios-rail', 'liveness'],
      variables: {
        email: () => `e2e-ios-life-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Lifecycle User',
        userToken: () => `ut-life-${Date.now()}`,
        sessionId: () => `ios-life-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start', 'Start Transcribe → 200',
          { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'transcribe', targetLang: 'ko', sourceLang: 'ko' }, [200]),
        {
          id: 'heartbeat-1', name: 'Heartbeat #1 → 200 alive', method: 'POST', path: '/realtime-session/heartbeat', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'transcribe' },
          expect: { status: [200] },
        },
        {
          id: 'heartbeat-2', name: 'Heartbeat #2 → 200 alive', method: 'POST', path: '/realtime-session/heartbeat', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'transcribe' },
          expect: { status: [200] },
        },
        {
          id: 'end', name: 'End → 200 finalized', method: 'POST', path: '/realtime-session/end', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'transcribe', routeGeneration: 1 },
          expect: { status: [200] },
        },
      ],
      expectedState: { lifecycle: 'quote 200 → start 200 → heartbeat 200 ×2 → end 200 finalized' },
    },
    // -- Points shortage ---------------------------------------------------
    {
      id: 'ios-points-shortage-402',
      name: 'No points → 402 usage_balance_insufficient (Buy points)',
      description: 'Replace the registration subscription with plan_free_v2 (no monthly points grant), so the tenant holds 0 points. The quote is still accepted (it reads only the catalog and the request), and the start is refused 402 usage_balance_insufficient with a positive usageShortfallMilli and options ["buy_pack"] — the one Meeting shortage contract; a points purchase is the remedy.',
      tags: ['ios', 'ios-rail', 'blocked'],
      variables: {
        email: () => `e2e-ios-short-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Points-less User',
        userToken: () => `ut-short-${Date.now()}`,
        sessionId: () => `ios-short-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        {
          id: 'seed-pointsless-subscription', name: 'Seed plan_free_v2 subscription (0 points)', method: 'POST', path: '/test/seed/subscription', auth: 'jwt',
          body: { tenantId: '{tenantId}', planId: 'plan_free_v2' },
          expect: { status: [200] },
        },
        IOS_CATALOG_STEP,
        ...iosQuotedStart('start-short', 'Start Lecture → 402 usage_balance_insufficient', { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' }, [402]),
      ],
      expectedState: {
        gate: 'quote 200, start 402 usage_balance_insufficient',
        body: 'usageShortfallMilli > 0 and options ["buy_pack"] (assert by reading the response; the engine checks status only)',
      },
    },
    // -- Bypass / negative rail (security) ---------------------------------
    {
      id: 'ios-bypass-unauth-401',
      name: 'No JWT → 401 on start / heartbeat / end',
      description: 'PR #1083 (S2.7) moved the realtime rail behind JwtMiddleware. With no Authorization header, each of start / heartbeat / end returns 401 — proving the rail is closed to anonymous callers. The tenant is taken from the JWT, never the body.',
      tags: ['ios', 'ios-bypass', 'security'],
      variables: {
        userToken: () => `ut-anon-${Date.now()}`,
        sessionId: () => `ios-anon-${Date.now()}`,
      },
      steps: [
        {
          id: 'start-unauth', name: 'Start without JWT → 401', method: 'POST', path: '/realtime-session/start', auth: 'none',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture' },
          expect: { status: [401] },
        },
        {
          id: 'heartbeat-unauth', name: 'Heartbeat without JWT → 401', method: 'POST', path: '/realtime-session/heartbeat', auth: 'none',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture' },
          expect: { status: [401] },
        },
        {
          id: 'end-unauth', name: 'End without JWT → 401', method: 'POST', path: '/realtime-session/end', auth: 'none',
          body: { sessionId: '{sessionId}', userToken: '{userToken}', scenario: 'lecture' },
          expect: { status: [401] },
        },
      ],
      expectedState: { security: 'all three realtime-session endpoints 401 without a JWT' },
    },
    {
      id: 'ios-bypass-legacy-mint-gone',
      name: 'Legacy POST /realtime/mint → 404 (route deleted)',
      description: 'S2.7 deleted the old POST /realtime/mint endpoint (the un-gated rail). It is no longer routable — expect 404. Guards against the old bypass being reintroduced.',
      tags: ['ios', 'ios-bypass', 'security'],
      variables: {},
      steps: [
        {
          id: 'legacy-mint', name: 'POST /realtime/mint → 404', method: 'POST', path: '/realtime/mint', auth: 'none',
          body: { sessionId: 'x', userToken: 'x' },
          expect: { status: [404] },
        },
      ],
      expectedState: { security: 'deleted mint route returns 404 (un-gated rail removed)' },
    },
    {
      id: 'ios-bypass-malformed-usertoken-400',
      name: 'Empty userToken → 400 (no row, no bypass)',
      description: 'With a valid JWT but an empty userToken, start fails validation with 400 (sessionId and userToken are required) — it does not open a session or fall through to an un-metered path.',
      tags: ['ios', 'ios-bypass'],
      variables: {
        email: () => `e2e-ios-malformed-${Date.now()}@test.meetingsync.ai`,
        password: 'TestPass123!',
        name: 'iOS Malformed User',
        sessionId: () => `ios-malformed-${Date.now()}`,
      },
      steps: [
        {
          id: 'register', name: 'Register User', method: 'POST', path: '/auth/register', auth: 'none',
          body: { email: '{email}', password: '{password}', name: '{name}' },
          extractors: { tenantId: 'user.tenantId', jwt: 'jwtToken' },
          expect: { status: [200, 201] },
        },
        {
          id: 'assign-ios-free', name: 'Assign plan_ios_free_v1', method: 'POST', path: '/test/seed/customer-plan', auth: 'function-key',
          body: { tenantId: '{tenantId}', targetPlanId: 'plan_ios_free_v1', reason: 'scenario-test' },
          expect: { status: [200] },
        },
        {
          id: 'start-malformed', name: 'Start with empty userToken → 400', method: 'POST', path: '/realtime-session/start', auth: 'jwt',
          body: { sessionId: '{sessionId}', userToken: '', scenario: 'lecture', targetLang: 'en', sourceLang: 'ko' },
          expect: { status: [400] },
        },
      ],
      expectedState: { security: 'malformed start returns 400, opens no session' },
    },
    // -- Backfill ----------------------------------------------------------
    {
      id: 'ios-backfill-dryrun-safe',
      name: 'iOS plan backfill — dry-run (writes nothing)',
      description: 'Runs the iOS plan backfill in dry-run mode (default) with a small sample limit. Returns 200 with a summary { scanned, candidates, updated, skipped, samples }; in dry-run, updated must be 0 and nothing is mutated. Review the sample tenants before any real (dryRun=false) run. NOTE: asserting the count fields requires the response-field `assert` engine extension (see plan Part C.3).',
      tags: ['ios', 'ios-backfill', 'superadmin'],
      variables: {},
      steps: [
        {
          id: 'backfill-dryrun', name: 'Backfill dry-run → 200 summary', method: 'POST', path: '/test/seed/ios-plan-backfill', auth: 'function-key',
          body: { dryRun: true, sampleLimit: 5 },
          expect: { status: [200] },
        },
      ],
      expectedState: {
        dryRun: 'true — no customer plan is mutated',
        review: 'inspect summary.samples; updated should be 0 in dry-run',
        nextStep: 'a real run (dryRun=false) is a separate, reviewed action — not in this scenario',
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/** @type {Array<object>} All scenario categories, ordered for sidebar display. */
export const SCENARIO_CATEGORIES = [
  AUTH_CATEGORY,
  FREE_TIER_CATEGORY,
  STARTER_CATEGORY,
  STANDARD_CATEGORY,
  PROFESSIONAL_CATEGORY,
  ADVANCED_CATEGORY,
  BUSINESS_CATEGORY,
  WEB_PLANS_CATEGORY,
  WEBINAR_PLANS_CATEGORY,
  EVENT_PASSES_CATEGORY,
  BILLING_PRIORITY_CATEGORY,
  SESSION_LIFECYCLE_CATEGORY,
  PLAN_LIMITS_CATEGORY,
  CLEANUP_CATEGORY,
  INCLUDED_PURCHASED_CATEGORY,
  PRICING_CATALOG_CATEGORY,
  PRODUCT_TYPE_GATES_CATEGORY,
  EVENTPASS_DISCOUNTS_CATEGORY,
  CUSTOMER_PORTAL_CATEGORY,
  SUBSCRIPTION_LIFECYCLE_CATEGORY,
  PAYMENT_FAILURES_CATEGORY,
  ACCOUNT_LIFECYCLE_CATEGORY,
  PASSWORD_AUTH_CATEGORY,
  MULTI_TENANT_CATEGORY,
  WEBHOOK_RELIABILITY_CATEGORY,
  CONCURRENT_SESSION_CATEGORY,
  IOS_REALTIME_BILLING_CATEGORY,
];
